// @ts-nocheck
import { message } from 'antd';
import { useColorizeWindow } from '@/features/ai-service/hooks/useColorizeWindow';
import { useDeepBottomRail } from '@/features/ai-service/hooks/useDeepBottomRail';
import { useGrillBlend } from '@/features/ai-service/hooks/useGrillBlend';
import { useCurrency } from '@/features/product-configurator/hooks/useCurrency';
import { getProductSetupDetail } from '@/features/product-configurator/api/productsSetup';
import { Obj } from '@/types';
import React, { ReactNode, createContext, useContext, useEffect, useRef, useState } from 'react';
import { BAY_POLES_DEFAULT, BOTH_SASHES_FIXED_VALUE, DIMENSION_OPTION, EXTERNAL_COLOR_OPTIONS, FOIL_SURCHARGE_RESOLVER, MESSAGE_TYPE_ENUM, OPTION_TYPE_ANGLED_CILL_HORN, OPTION_TYPE_CILL, OPTION_TYPE_COUPLING_CLIP, OPTION_TYPE_GLAZING_TYPE, OPTION_TYPE_HARDWARE_ITEM, OPTION_TYPE_HARDWARE_REQUIRED, OPTION_TYPE_HARDWARE_TYPE, OPTION_TYPE_INSIDE_SPRAY_COLOUR, OPTION_TYPE_OUTSIDE_SPRAY_COLOUR, OPTION_TYPE_SASH, OPTION_TYPE_SECURITY, OPTION_TYPE_SPRAY, OPTION_TYPE_TRANSOM_DROP, OPTION_TYPE_VARIATION, OPTION_TYPE_WINDOW_TYPE, PROFILE_COLOR_OPTION, ProductStructureType } from '../constants';
import { useGetDBRAnswer, useGetTemplateColors } from '../hooks';
import { GRILL_OPTION_TYPES, useGrillAnswers } from '../hooks/useGrillAnswers';
import { IAssistantCart, IAssistantConfig, IAssistantMessage, IQuestion, IQuestionOption, ISurgeItem, IWindowCartItem } from '../types';
import { COUPLING_CLIP_MSG_ID, DIMENSION_ALERT_ID, SPRAY_COLOUR_ALERT_ID, buildCouplingClipMessage, calculateBayPolesAndCouplingClips } from '../utils/bayWindowCalculations';
import { calculatePriceFromMessages } from '../utils/configurationPriceCalculation';
import { filterQuestionOptions, getFormFieldDependentQuestions, getFormValuesFromMessages, getNextVisibleDependentQuestion, getNextVisibleQuestion, getSkipQuestionIdsQuestion, getSkippedTrickleVentFrames, rebakeMessagesWithLatestState, resolveVentWidth, shouldShowQuestion } from '../utils/displayCondition';
import { FireEgressWindowType, OPTION_TYPE_CUSTOM_FRACTION, OPTION_TYPE_NUMERIC_VALUES, calculateFireEgress, calculateFireEgressMultiFrame, extractDimensionsFromMessages, resolveSashHeights } from '../utils/fireEgress';
import { applyGlazingPriceToMessages } from '../utils/glazingPriceUpdate';
import { removeOldDependentQuestions } from '../utils/messageComparison';
import { evaluateAllSurgePricesFromMessages, evaluateOptionBasedRules, evaluateValueBasedRules, formatSurgeMessage } from '../utils/pricingCalculator';
import { getProductFromOption } from '../utils/product';
import { ARCH_FOIL_COLOUR_WIDTH_MIN, isSweptArchProduct, shouldShowArchProfileColourOption, shouldShowSweptArchFoilOption } from '../utils/profileColour';
import { findAndSortQuestionsForProductChange, logQuestionHierarchy } from '../utils/questionDependency';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

interface ConfigurationDrawerState {
    isOpen: boolean;
    editingItem: IWindowCartItem | null;
    editingItemId: string | null;
    editingQuestionId: string | null;
    draftMessages: IAssistantMessage[];
    questionsToReAnswerList: string[];
    questionsToRemoveFromOriginal: string[];
    isEditingComplete: boolean;
    isPreviewModalOpen: boolean;
    blendedImageUrl: string | null;
    onLineBlendImageUrl: string | null;
    isBlending: boolean;
    hasProductChanged: boolean;
    config: IAssistantConfig;
    questionFlow: IQuestion[];
    marginPercentage: number;
    egressConsentState: null | 'pending' | 'accepted' | 'rejected';
    /** The product currently in effect for the edit (selected mid-edit if changed,
     *  otherwise the original cart item's product). Drives product-aware UI such
     *  as the couple-window dimension splitter. */
    selectedProduct?: IQuestionOption;
}

interface ConfigurationDrawerMethods {
    openDrawer: (itemId: string, item: IWindowCartItem, questionIdToEdit: string, cart: IAssistantCart) => void;
    closeDrawer: () => void;
    handleSingleSelect: (questionId: string, option: IQuestionOption) => void;
    handleMultipleChoice: (questionId: string, options: IQuestionOption[]) => void;
    handleFormSubmit: (questionId: string, formValues: Obj) => void;
    handleTextInput: (questionId: string, textValue: string) => void;
    saveChanges: (onSave: (updatedItem: IWindowCartItem) => void | Promise<void>) => Promise<void>;
    discardChanges: () => void;
    openPreviewModal: () => void;
    closePreviewModal: () => void;
    setBlendMode: (mode: 'offline' | 'online') => void;
    acceptEgressConsent: () => void;
    rejectEgressConsent: () => void;
}

interface ConfigurationDrawerContextType {
    state: ConfigurationDrawerState;
    method: ConfigurationDrawerMethods;
}

const ConfigurationDrawerContext = createContext<ConfigurationDrawerContextType | undefined>(undefined);

interface ConfigurationDrawerProviderProps {
    children: ReactNode;
    questionFlow: IQuestion[];
    config: IAssistantConfig;
    marginPercentage?: number;
}

export const ConfigurationDrawerProvider: React.FC<ConfigurationDrawerProviderProps> = ({
    children,
    questionFlow,
    config,
    marginPercentage
}) => {
    // State
    const [isOpen, setIsOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<IWindowCartItem | null>(null);
    const [editingItemId, setEditingItemId] = useState<string | null>(null);
    const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
    const [draftMessages, setDraftMessages] = useState<IAssistantMessage[]>([]);
    const [questionsToReAnswerList, setQuestionsToReAnswerList] = useState<string[]>([]);
    const [questionsToRemoveFromOriginal, setQuestionsToRemoveFromOriginal] = useState<string[]>([]);
    const [editedItemProduct, setEditedProduct] = useState<IQuestionOption>()
    const [selectedProduct, setSelectedProduct] = useState<IQuestionOption>();
    const [isEditingComplete, setIsEditingComplete] = useState(false);
    const [cart, setCart] = useState<IAssistantCart>();
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [isEnableBlend, setIsEnableBlend] = React.useState<boolean>(false)
    const { formatPrice } = useCurrency();
    const [currentBlendMode, setCurrentBlendMode] = useState<'offline' | 'online'>('offline');
    const [egressConsentState, setEgressConsentState] = useState<null | 'pending' | 'accepted' | 'rejected'>(null);
    const [egressConsentAfterQuestionId, setEgressConsentAfterQuestionId] = useState<string | null>(null);
    const pendingDependentRef = useRef<{
        questionId: string;
        dependentQuestions: IQuestion[];
        currentAnswers: Record<string, IQuestionOption[]>;
        currentProduct: IQuestionOption | undefined;
        isUpdate: boolean;
    } | null>(null);

    // Merge original and draft messages, giving draft answers priority over the original
    // so that color/option changes in the drawer are reflected immediately.
    const messagesForColorDetection = React.useMemo(() => {
        const draftQuestionIds = new Set(
            draftMessages
                .filter(m => m.question?.questionId)
                .map(m => m.question.questionId)
        );
        const originalFiltered = (editingItem?.messages ?? []).filter(
            m => !m.question?.questionId || !draftQuestionIds.has(m.question.questionId)
        );
        return [...originalFiltered, ...draftMessages];
    }, [editingItem?.messages, draftMessages]);
    const { color } = useGetTemplateColors({
        messages: messagesForColorDetection,
    })

    // ── DBR blending (1st in pipeline) — runs on raw product thumbnail ────────
    const storedThumbnail = selectedProduct?.thumbnail || editedItemProduct?.thumbnail || null;

    // The stored thumbnail can be a device-local cache path (e.g.
    // file:///data/user/0/.../template-cache/<guid>.png) left over from whichever
    // device originally cached it during quote creation — that file is unreachable
    // from here. When that happens, re-resolve the product's real hosted image via
    // its product record instead of trying (and failing) to fetch the local path.
    const [resolvedTemplateUrl, setResolvedTemplateUrl] = useState<string | null>(storedThumbnail);
    useEffect(() => {
        let cancelled = false;
        if (!storedThumbnail || !storedThumbnail.startsWith('file://')) {
            setResolvedTemplateUrl(storedThumbnail);
            return;
        }
        const productId = (selectedProduct ?? editedItemProduct)?.optionId;
        if (!productId) {
            setResolvedTemplateUrl(null);
            return;
        }
        getProductSetupDetail(productId)
            .then((product) => {
                if (!cancelled) setResolvedTemplateUrl(product?.dogma_productimageurl || null);
            })
            .catch(() => {
                if (!cancelled) setResolvedTemplateUrl(null);
            });
        return () => { cancelled = true; };
    }, [storedThumbnail, selectedProduct?.optionId, editedItemProduct?.optionId]);

    // Pipeline signature: which stages are currently needed. Computed directly
    // (not read back from dbrBlend/grillBlend's own outputs, to avoid a
    // circular dependency on the hooks we're about to call) so it's a pure,
    // immediate reflection of the current messages/color — independent of
    // each hook's own internal timing.
    //
    // Passed as `forceKey` into ALL THREE blend hooks below. Without this,
    // each stage only re-verifies when its OWN direct inputs change; when one
    // stage turns on/off (grill -> None at any level, DBR turned off, color
    // removed), the other stages have no reliable signal to re-run against
    // the new upstream state — they only happen to cascade correctly when an
    // output URL value changes, which isn't guaranteed. This signature
    // guarantees a genuine dependency change for every stage whenever ANY
    // stage's active/inactive status flips.
    const { isDbrEnabled: dbrEnabledPrecheck } = useGetDBRAnswer({ messages: messagesForColorDetection });
    const grillWindowsPrecheck = useGrillAnswers(messagesForColorDetection);
    const grillNeedsToRunPrecheck = grillWindowsPrecheck.some((w) => w.topSash || w.bottomSash);
    const pipelineSignature = `dbr:${dbrEnabledPrecheck ? 1 : 0}|grill:${grillNeedsToRunPrecheck ? 1 : 0}|color:${color ? 1 : 0}`;

    const rawTemplateUrl = resolvedTemplateUrl;
    const dbrBlend = useDeepBottomRail(
        messagesForColorDetection,
        rawTemplateUrl,
        selectedProduct || editedItemProduct,
        isEnableBlend,
        pipelineSignature,
    );

    // DBR settled: either DBR is not needed, or it has produced an image and stopped.
    // The dbrImageUrl=null check covers the debounce window (isApplying=false but
    // the effect hasn't fired yet) so colorize doesn't start too early.
    const dbrNeedsToRun = dbrBlend.isDbrEnabled;
    const dbrSettled = !dbrNeedsToRun || (!dbrBlend.isApplying && dbrBlend.lastTaskStatus?.result_url !== null);
    // ── Colorize (2nd in pipeline) — tints window frame with profile color hex, after DBR settles ─
    const colorizeInputUrl = dbrSettled
        ? (dbrBlend.lastTaskStatus?.result_url ?? rawTemplateUrl)
        : null;
    const colorizeBlend = useColorizeWindow({
        templateUrl: colorizeInputUrl,
        colorHex: color || null,
        // Gated on isEnableBlend, same as DBR and grill — this flag is
        // exactly what openDrawer sets false (and only an actual grill/
        // color/product/DBR re-answer sets true) specifically to prevent
        // the pipeline from running just because the drawer opened.
        // Hardcoding this true meant colorize ignored that gate entirely:
        // any incidental key change (e.g. DBR producing a fresh blob: URL
        // on re-open, even for the same unchanged product) made colorize
        // actually re-run, visibly "re-blending" with nothing touched.
        enabled: isEnableBlend,
        selectedProduct: selectedProduct || editedItemProduct,
        forceKey: pipelineSignature,
    });
    const colorizedTemplateUrl = colorizeBlend.colorizedUrl ?? colorizeInputUrl;
    // ── Grill blending (3rd in pipeline, waits for DBR + colorize) ───────────
    const grillWindowUrl = (dbrSettled && !colorizeBlend.isApplying)
        ? (colorizedTemplateUrl ?? null)
        : null;
    const grillBlend = useGrillBlend(messagesForColorDetection, grillWindowUrl, isEnableBlend, color || null, pipelineSignature, (selectedProduct ?? editedItemProduct)?.numberOfFrames ?? 1);

    const grillNeedsToRun = grillBlend.grillWindows.some((w: any) => w.topSash || w.bottomSash);

    // No property photo is ever attached in this drawer's edit flow, so
    // useBlendAiService's house-photo AI compositing never actually fires
    // here (its `enabled` always requires a propertyImageUrl) — it only added
    // an extra async layer whose stale state could mask the already-correct
    // grill/colorize/DBR template chain below. The live preview and the saved
    // image both read that chain directly instead.

    const getAnswersFromMessages = (messages: IAssistantMessage[]): Record<string, IQuestionOption[]> => {
        const answersMap: Record<string, IQuestionOption[]> = {};
        messages.forEach(msg => {
            if (msg.type === 'user' && msg.question?.category && msg.response?.answer) {
                const categoryId = msg.question.category;
                if (!answersMap[categoryId]) answersMap[categoryId] = [];
                answersMap[categoryId].push(...msg.response.answer);

                // Also index by frame (e.g. "catId.frame2") so per-frame dependency
                // checks use only that frame's answer and avoid cross-frame false positives.
                const frameMatch = msg.question.responsePath?.match(/frame(\d+)$/i);
                if (frameMatch?.[1]) {
                    const frameKey = `${categoryId}.frame${frameMatch[1]}`;
                    if (!answersMap[frameKey]) answersMap[frameKey] = [];
                    answersMap[frameKey].push(...msg.response.answer);
                }
            }
        });
        return answersMap;
    };

    /**
     * Build a category-indexed answers map by merging original item messages with the
     * current draft messages on a per-question basis.
     *
     * Multiple questions can share the same category. A naive category-level merge
     * (spreading one answers map over another) would overwrite ALL options in a
     * category whenever any one question in that category is re-answered.
     * This function avoids that by:
     *   1. Collecting per-question answers from original messages.
     *   2. Overriding only the questions that exist in updatedMessages (draft).
     *   3. Re-building the category map from the merged per-question view.
     */
    const getMergedAnswers = (
        originalMessages: IAssistantMessage[],
        updatedMessages: IAssistantMessage[],
    ): Record<string, IQuestionOption[]> => {
        // Build per-question answer index from original messages
        const byQuestion: Record<string, { category: string; options: IQuestionOption[] }> = {};
        originalMessages.forEach(msg => {
            if (msg.type === 'user' && msg.question?.questionId && msg.question?.category && msg.response?.answer?.length) {
                byQuestion[msg.question.questionId] = {
                    category: msg.question.category,
                    options: msg.response.answer,
                };
            }
        });

        // Override with draft answers (per-question, so only the changed question is replaced)
        updatedMessages.forEach(msg => {
            if (msg.type === 'user' && msg.question?.questionId && msg.question?.category && msg.response?.answer?.length) {
                byQuestion[msg.question.questionId] = {
                    category: msg.question.category,
                    options: msg.response.answer,
                };
            }
        });

        // Rebuild category-indexed map from the merged per-question view.
        // Also build frame-specific keys (e.g. "catId.frame1") so resolveDepAnswers
        // finds per-frame answers when checking option display conditions — mirrors
        // what getAnswersFromMessages does via responsePath.
        const allMsgs = [...originalMessages, ...updatedMessages];
        const answersMap: Record<string, IQuestionOption[]> = {};
        Object.entries(byQuestion).forEach(([questionId, { category, options }]) => {
            if (!answersMap[category]) answersMap[category] = [];
            answersMap[category].push(...options);

            const src = allMsgs.findLast?.(m => m.question?.questionId === questionId) ??
                [...allMsgs].reverse().find(m => m.question?.questionId === questionId);
            const frameMatch = src?.question?.responsePath?.match(/frame(\d+)$/i);
            if (frameMatch?.[1]) {
                const frameKey = `${category}.frame${frameMatch[1]}`;
                if (!answersMap[frameKey]) answersMap[frameKey] = [];
                answersMap[frameKey].push(...options);
            }
        });
        return answersMap;
    };

    /**
     * Build currentAnswers for rendering re-ask questions, excluding answers from
     * questions that are being removed (questionsToRemoveFromOriginal + editingQuestionId).
     * Without this, removed questions' answers (e.g. Glazing) still influence priceList
     * resolution for downstream questions (e.g. Sash), causing prices to show as 0.
     */
    const getAnswersExcludingRemoved = (updatedMessages: IAssistantMessage[]) => {
        const excludedIds = new Set([
            editingQuestionId ?? '',
            ...questionsToRemoveFromOriginal,
        ]);
        const filteredOriginal = (editingItem?.messages ?? []).filter(
            msg => !msg.question?.questionId || !excludedIds.has(msg.question.questionId),
        );
        return getMergedAnswers(filteredOriginal, updatedMessages);
    };

    /**
     * Enrich a question with pricing rules from the original cart item messages if available
     * This ensures dependent questions have pricing rules even if questionFlow doesn't include them
     */
    const enrichQuestionWithPricingRules = (question: IQuestion): IQuestion => {
        if (!editingItem) return question;

        // Try to find this question in the original messages
        const originalMessage = editingItem.messages.find(
            msg => msg.question?.questionId === question.questionId
        );

        if (originalMessage?.question?.priceConditions) {
            return {
                ...question,
                priceConditions: originalMessage.question.priceConditions,
                // Also enrich options with pricing rules
                options: question.options?.map(opt => {
                    const originalOpt = originalMessage.question.options?.find(o => o.optionId === opt.optionId);
                    if (originalOpt?.priceConditions) {
                        return {
                            ...opt,
                            priceConditions: originalOpt.priceConditions
                        };
                    }
                    return opt;
                })
            };
        }

        return question;
    };

    const getSelectedProductFromMessages = (messages: IAssistantMessage[]): IQuestionOption | undefined => {
        let productOption: IQuestionOption | undefined;
        messages.forEach((msg) => {
            if (msg.type !== 'user') return;
            (msg.response?.answer ?? []).forEach((option) => {
                const product = getProductFromOption(option);
                // Guard: real product UUIDs are long and never start with "OPTION".
                // Pseudo-options like OPTION-WINDOW-NAME share optionType 0 but must
                // not be treated as the selected product or they corrupt validForProducts checks.
                if (!product) return;
                if (product.optionId.length <= 10 || product.optionId.includes('OPTION')) return;
                // Prefer WINDOW_TYPE (optionType 0) over VARIATION (optionType 64):
                // validForProducts in the admin config typically references the window-type UUID.
                if (!productOption || option.optionType === OPTION_TYPE_WINDOW_TYPE) {
                    productOption = option;
                }
            });
        });
        return productOption;
    };




    // ========================================================================
    // MESSAGE MANAGEMENT
    // ========================================================================

    const createUserResponseMessage = (
        question: IQuestion,
        answer: IQuestionOption[],
        form?: Obj
    ): IAssistantMessage => {
        const selectedIds = answer.map(opt => opt.optionId);

        // Format message based on whether it's a form submission or option selection
        let message = '';
        if (form && Object.keys(form).length > 0) {
            // Format form values as "Field: Value". Skip metadata keys (e.g.
            // __split_mode__) which exist only for UI-state restoration.
            message = Object.entries(form)
                .filter(([key]) => !key.startsWith('__'))
                .map(([key, value]) => {
                    const option = answer.find(opt => opt.optionId === key || opt.responseValue === key);
                    const label = option?.optionText || key;
                    return `${label}: ${value}`;
                })
                .join(', ');
        } else {
            // For regular options, just show the option text
            message = answer.map(opt => opt.optionText).join(', ');
        }

        return {
            id: `user_${Date.now()}_${Math.random().toString(36)}`,
            type: 'user',
            message: message,
            question: question,
            response: {
                answer: answer,
                selectedOptionsIds: selectedIds,
                form: form || {}
            }
        };
    };

    // ========================================================================
    // FIRE EGRESS HELPERS
    // ========================================================================

    const TRANSOM_FRACTION_RESP = 'custom_fraction_from_top';
    const TRANSOM_NUMERIC_RESP = 'numeric_value';

    const isTransomDropChainEnd = (questionId: string, msgs: IAssistantMessage[]): boolean => {
        const msg = msgs.find(m => m.question?.questionId === questionId);
        if (!msg) return false;
        const optType = msg.question?.optionType;
        const hasResp = (msg.response?.answer?.length ?? 0) > 0 ||
            !!(msg.response?.form && Object.keys(msg.response.form).length > 0);
        if (!hasResp) return false;

        if (optType === OPTION_TYPE_CUSTOM_FRACTION)
            return msgs.some(m => m.question?.optionType === OPTION_TYPE_TRANSOM_DROP);

        if (optType === OPTION_TYPE_NUMERIC_VALUES)
            return msgs.some(m =>
                m.question?.optionType === OPTION_TYPE_TRANSOM_DROP &&
                (m.response?.answer?.[0]?.optionType === OPTION_TYPE_NUMERIC_VALUES ||
                    m.response?.answer?.[0]?.responseValue === TRANSOM_NUMERIC_RESP)
            );

        if (optType === OPTION_TYPE_TRANSOM_DROP) {
            const selectedOpt = msg.response?.answer?.[0];
            if (selectedOpt?.optionType === OPTION_TYPE_CUSTOM_FRACTION) return false;
            if (selectedOpt?.optionType === OPTION_TYPE_NUMERIC_VALUES) return false;
            const sel = selectedOpt?.responseValue;
            if (sel === TRANSOM_FRACTION_RESP || sel === TRANSOM_NUMERIC_RESP) return false;
            const nextQ = getNextVisibleQuestion(
                questionId,
                questionFlow,
                getAnswersFromMessages(msgs),
                getSelectedProductFromMessages(msgs),
                false,
                getFormValuesFromMessages(msgs),
            );
            if (nextQ?.optionType === OPTION_TYPE_CUSTOM_FRACTION) return false;
            if (nextQ?.optionType === OPTION_TYPE_NUMERIC_VALUES) return false;
            return true;
        }
        return false;
    };

    type FrameEgressDetail = {
        frameIndex: number;
        frameWidth: number;
        pass: 'sliding' | 'tilting' | 'both' | 'none' | 'not_applicable';
    };

    type EgressCheckResult =
        | { fails: true; frameDetails?: FrameEgressDetail[] }
        | { fails: false; pass: 'sliding' | 'tilting' | 'both'; multiFrame: boolean; frameDetails?: FrameEgressDetail[] }
        | null;

    const checkFireEgress = (msgs: IAssistantMessage[]): EgressCheckResult => {
        const dims = extractDimensionsFromMessages(msgs);
        const product = selectedProduct ?? editedItemProduct;
        const ptc = product?.producttypecode;
        if (ptc === ProductStructureType.Arch) return null;
        const wType: FireEgressWindowType = ptc === ProductStructureType.Coupled ? 'couple'
            : ptc === ProductStructureType.Bays ? 'bay'
                : 'standard';
        if (!dims.windowHeight) return null;
        const { topSash, bottomSash } = resolveSashHeights('', dims.windowHeight, dims.topSash, dims.bottomSash);

        if (dims.frameWidths && dims.frameWidths.length > 1) {
            const multi = calculateFireEgressMultiFrame(dims.frameWidths, wType, dims.windowHeight, topSash, bottomSash, dims.frameSashOperations);
            const frameDetails: FrameEgressDetail[] = multi.frames.map(f => ({
                frameIndex: f.frameIndex,
                frameWidth: f.frameWidth,
                pass: !f.result.applicable ? 'not_applicable' : f.result.pass,
            }));
            if (!multi.anyCompliant) return { fails: true, frameDetails };
            const firstPass = multi.frames.find(f => f.result.applicable && f.result.pass !== 'none');
            const pass = (firstPass?.result as any)?.pass ?? 'sliding';
            return { fails: false, pass, multiFrame: true, frameDetails };
        }
        if (dims.windowWidth) {
            const result = calculateFireEgress({ windowType: wType, windowWidth: dims.windowWidth, windowHeight: dims.windowHeight, topSash, bottomSash, sashOperation: dims.sashOperation });
            if (!result.applicable) return null;
            if (result.pass === 'none') return { fails: true };
            return { fails: false, pass: result.pass, multiFrame: false };
        }
        return null;
    };

    const buildEgressAlertMessage = (egressResult: EgressCheckResult): IAssistantMessage | null => {
        if (!egressResult) return null;
        const id = `egress_alert_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const baseMsg = {
            id,
            question: {} as any,
            response: { answer: [], selectedOptionsIds: [], form: {} },
        };

        const alertItems = egressResult.frameDetails?.map(f => {
            if (f.pass === 'not_applicable') return { label: `Frame ${f.frameIndex} (${f.frameWidth}mm)`, statusVariant: 'info' as const, detail: 'Not applicable' };
            if (f.pass === 'none') return { label: `Frame ${f.frameIndex} (${f.frameWidth}mm)`, statusVariant: 'error' as const, detail: 'Not compliant' };
            return { label: `Frame ${f.frameIndex} (${f.frameWidth}mm)`, statusVariant: 'success' as const, detail: `Compliant — ${f.pass === 'both' ? 'sliding/tilting' : f.pass === 'sliding' ? 'sliding' : 'tilting'}` };
        });

        if (egressResult.fails) {
            return {
                ...baseMsg,
                type: 'alert' as const,
                alertVariant: 'error' as const,
                alertTitle: 'Fire Egress Not Compliant',
                message: 'This window does not meet fire egress regulations based on the dimensions entered. If you believe this is incorrect or need further guidance, please contact our internal sales team before proceeding. Do you still want it quoted?',
                alertItems,
                alertActions: [
                    { label: 'Continue Anyway', actionId: 'egress_accept', variant: 'primary' as const },
                    { label: 'Cancel', actionId: 'egress_reject', variant: 'secondary' as const },
                ],
            };
        }
        const label = egressResult.pass === 'sliding' ? 'when sliding' : egressResult.pass === 'both' ? 'when sliding or tilting' : 'when bottom sash tilts';
        const suffix = egressResult.multiFrame ? ' (at least one frame compliant)' : '';
        return {
            ...baseMsg,
            type: 'alert' as const,
            alertVariant: 'success' as const,
            alertTitle: 'Fire Egress Compliant',
            message: `Window meets fire egress regulations ${label}${suffix}.`,
            alertItems,
        };
    };

    const acceptEgressConsent = () => {
        setEgressConsentState('accepted');
        const msgsWithoutAlert = draftMessages.filter(m => !m.id.startsWith('egress_alert_'));
        setDraftMessages(msgsWithoutAlert);
        if (!pendingDependentRef.current) return;
        const { questionId, dependentQuestions, currentAnswers, currentProduct, isUpdate } = pendingDependentRef.current;
        pendingDependentRef.current = null;
        handleDependentQuestions(questionId, dependentQuestions, currentAnswers, currentProduct, isUpdate);
    };

    const rejectEgressConsent = () => {
        setEgressConsentState('rejected');
        setDraftMessages(prev => prev.map(m =>
            m.id.startsWith('egress_alert_')
                ? {
                    ...m,
                    alertVariant: 'warning' as const,
                    alertTitle: 'Adjustment Required',
                    message: 'Please adjust the window dimensions above to meet fire egress requirements.',
                    alertActions: undefined,
                    alertItems: undefined,
                }
                : m
        ));
    };

    // ========================================================================
    // SKIP ALERTS (spray, trickle vents)
    // ========================================================================

    const injectSpraySkipAlert = (
        currentProduct: IQuestionOption | undefined,
        currentQuestionId: string,
        nextQuestionId: string | undefined,
    ) => {
        if (currentProduct?.producttypecode !== ProductStructureType.Arch) return;
        const sprayQ = questionFlow.find(q => q.optionType === OPTION_TYPE_SPRAY);
        if (!sprayQ) return;
        const qIds = questionFlow.map(q => q.questionId);
        const curIdx = qIds.indexOf(currentQuestionId);
        const nxtIdx = nextQuestionId ? qIds.indexOf(nextQuestionId) : questionFlow.length;
        const sprayIdx = qIds.indexOf(sprayQ.questionId);
        const passedHere = sprayIdx >= 0 &&
            (curIdx < 0 || sprayIdx > curIdx) &&
            sprayIdx < nxtIdx;
        if (!passedHere) return;
        const currentFV = getFormValuesFromMessages([...(editingItem?.messages ?? []), ...draftMessages]);
        const width = resolveVentWidth(currentFV);
        if (width <= 0 || width >= ARCH_FOIL_COLOUR_WIDTH_MIN) return;
        const alertId = `spray_skip_alert_arch`;
        setDraftMessages(prev => {
            if (prev.some(m => m.id === alertId)) return prev;
            return [...prev, {
                id: alertId,
                type: 'alert' as const,
                alertVariant: 'info' as const,
                alertTitle: 'Spray Colour Not Available',
                message: `Spray colour is only available for arch windows with Foil profile colour (minimum ${ARCH_FOIL_COLOUR_WIDTH_MIN}mm wide). As this window is ${width}mm wide with Standard White profile, spray colour has been skipped.`,
                question: {} as any,
                response: { answer: [], selectedOptionsIds: [], form: {} },
            }];
        });
    };

    const injectTrickleVentSkipAlerts = (
        currentAnswers: Record<string, IQuestionOption[]>,
        currentProduct: IQuestionOption | undefined,
        currentQuestionId: string,
        nextQuestionId: string | undefined,
    ) => {
        const currentFV = getFormValuesFromMessages([
            ...(editingItem?.messages ?? []),
            ...draftMessages,
        ]);
        const skipped = getSkippedTrickleVentFrames(questionFlow, currentFV, currentAnswers, currentProduct);
        if (skipped.length === 0) return;

        const qIds = questionFlow.map(q => q.questionId);
        const curIdx = qIds.indexOf(currentQuestionId);
        const nxtIdx = nextQuestionId ? qIds.indexOf(nextQuestionId) : questionFlow.length;

        for (const info of skipped) {
            const trickleIdx = qIds.indexOf(info.questionId);
            const passedHere = trickleIdx >= 0 &&
                (curIdx < 0 || trickleIdx > curIdx) &&
                trickleIdx < nxtIdx;
            if (!passedHere) continue;
            const alertId = `trickle_vent_skip_alert_${info.questionId}`;
            const reason = info.widthMin !== undefined && info.frameWidth < info.widthMin
                ? `The frame width (${info.frameWidth}mm) is below the minimum required width of ${info.widthMin}mm for trickle vent installation.`
                : `The frame width (${info.frameWidth}mm) exceeds the maximum allowed width of ${info.widthMax}mm for trickle vent installation.`;
            setDraftMessages(prev => {
                if (prev.some(m => m.id === alertId)) return prev;
                return [...prev, {
                    id: alertId,
                    type: 'alert' as const,
                    alertVariant: 'info' as const,
                    alertTitle: `Trickle Vents Not Available — ${info.frameLabel}`,
                    message: `Trickle vents have been skipped for ${info.frameLabel}. ${reason}`,
                    question: {} as any,
                    response: { answer: [], selectedOptionsIds: [], form: {} },
                }];
            });
        }
    };

    // ========================================================================
    // ANSWER HANDLERS
    // ========================================================================

    const handleDependentQuestions = (
        questionId: string,
        dependentQuestions: IQuestion[],
        currentAnswers: Record<string, IQuestionOption[]>,
        currentProduct?: IQuestionOption,
        isUpdate: boolean = false, // Flag to indicate if this is updating an existing answer
        freshMessages?: IAssistantMessage[], // Pass post-setDraftMessages array to avoid stale closure
    ) => {
        // Check if we're answering a dependent question from the list
        const isAnsweringDependentQuestion = questionsToReAnswerList.includes(questionId);
        if (isAnsweringDependentQuestion || isUpdate) {


            // Remove the just-answered question from the list
            let updatedList = questionsToReAnswerList.filter(id => id !== questionId);
            // Add newly found dependent questions to the list.
            // Never filter by already-answered IDs — if a question re-appears as
            // a dependent after re-answering an earlier one, the user must see it
            // again. Loop prevention is handled naturally: each question is removed
            // from updatedList as soon as it is answered (line above).
            if (dependentQuestions.length > 0) {
                const newDependentIds = dependentQuestions.map(q => q.questionId);
                updatedList = [...new Set([...updatedList, ...newDependentIds])];
            }

            setQuestionsToReAnswerList(updatedList);
            setQuestionsToRemoveFromOriginal(prev => {
                const uniqueIds = new Set([...prev, ...updatedList]);
                return Array.from(uniqueIds);
            });

            // Show next question if any remain
            if (updatedList.length > 0) {

                const questionList = questionFlow.filter((item) => updatedList.includes(item.questionId));
                const currentFormValues = getFormValuesFromMessages([
                    ...(editingItem?.messages ?? []),
                    ...(freshMessages ?? draftMessages),
                ]);
                let nextQuestion = getNextVisibleQuestion(
                    questionId,
                    questionList ?? [],
                    currentAnswers,
                    currentProduct,
                    true,
                    currentFormValues
                );
                const skipList = getSkipQuestionIdsQuestion(
                    questionId,
                    questionList ?? [],
                    currentAnswers,
                    currentProduct,
                    true,
                    currentFormValues
                )
                updatedList = updatedList.filter((item) => !skipList.includes(item))

                setQuestionsToReAnswerList(updatedList);
                if (nextQuestion) {
                    // A question is being shown — editing is NOT complete yet regardless of updatedList
                    setIsEditingComplete(false);

                    // Enrich with pricing rules from original if available
                    nextQuestion = enrichQuestionWithPricingRules(nextQuestion);
                    const filteredQuestion = filterQuestionOptions(nextQuestion, currentAnswers, currentProduct, currentFormValues);

                    // Pre-populate with existing answer from original item if available
                    const existingUserMsg = editingItem?.messages.find(
                        msg => msg.type === 'user' && msg.question?.questionId === nextQuestion!.questionId
                    );

                    const questionMessage: IAssistantMessage = {
                        id: `edit-bot-${Date.now()}-${nextQuestion.questionId}`,
                        type: 'assistant',
                        message: filteredQuestion.question,
                        question: filteredQuestion,
                        response: existingUserMsg?.response ?? { answer: [], selectedOptionsIds: [], form: {} }
                    };

                    // Inject hardware skip alert at the exact position where hardware would have rendered.
                    const hardwareRequiredYes_1 = Object.values(currentAnswers).some(opts =>
                        opts.some(o => o.optionType === OPTION_TYPE_HARDWARE_REQUIRED && o.responseValue === 'yes')
                    );
                    const allHardwareTypeQs_1 = questionFlow.filter(q => q.optionType === OPTION_TYPE_HARDWARE_TYPE);
                    const anyHardwareTypeVisible_1 = allHardwareTypeQs_1.some(q =>
                        shouldShowQuestion(q, currentAnswers, currentProduct, false, currentFormValues)
                    );
                    if (!hardwareRequiredYes_1 && !anyHardwareTypeVisible_1 && allHardwareTypeQs_1.length > 0) {
                        const qIds = questionFlow.map(q => q.questionId);
                        const curIdx = qIds.indexOf(questionId);
                        const nxtIdx = qIds.indexOf(nextQuestion.questionId);
                        const hwIdx = qIds.indexOf(allHardwareTypeQs_1[0].questionId);
                        const hardwarePassedHere = hwIdx >= 0 &&
                            (curIdx < 0 || hwIdx > curIdx) &&
                            hwIdx < nxtIdx;
                        if (hardwarePassedHere) {
                            const hardwareRequiredNo = Object.values(currentAnswers).some(opts =>
                                opts.some(o => o.optionType === OPTION_TYPE_HARDWARE_REQUIRED && o.responseValue === 'no')
                            );
                            const bothSashesFixed = Object.values(currentAnswers).some(opts =>
                                opts.some(o => o.optionType === OPTION_TYPE_SASH && o.responseValue === 'both_sashes_fixed')
                            );
                            const skipMessage = hardwareRequiredNo
                                ? 'You selected No for Hardware Required. As a result, all hardware questions have been skipped.'
                                : bothSashesFixed
                                ? 'You have selected Both Sashes Fixed for all frames. As a result, no hardware is required and the hardware selection has been skipped.'
                                : 'Hardware is not applicable for this configuration and has been skipped.';
                            setDraftMessages(prev => {
                                if (prev.some(m => m.id?.startsWith('hardware_skip_alert_'))) return prev;
                                return [...prev, {
                                    id: `hardware_skip_alert_${Date.now()}`,
                                    type: 'alert' as const,
                                    alertVariant: 'info' as const,
                                    alertTitle: 'No Hardware Required',
                                    message: skipMessage,
                                    question: {} as any,
                                    response: { answer: [], selectedOptionsIds: [], form: {} },
                                }];
                            });
                        }
                    }

                    injectTrickleVentSkipAlerts(currentAnswers, currentProduct, questionId, nextQuestion.questionId);
                    injectSpraySkipAlert(currentProduct, questionId, nextQuestion.questionId);
                    if (nextQuestion.optionType === OPTION_TYPE_HARDWARE_REQUIRED) {
                        setDraftMessages(prev => {
                            if (prev.some(m => m.id === 'hardware_required_alert')) return prev;
                            return [...prev, {
                                id: 'hardware_required_alert',
                                type: 'alert' as const,
                                alertVariant: 'info' as const,
                                alertTitle: 'Hardware on Fixed Sash',
                                message: 'This window has a fixed sash. Hardware is not normally required for fixed sash windows, but you can choose to add it if needed.',
                                question: {} as any,
                                response: { answer: [], selectedOptionsIds: [], form: {} },
                            }];
                        });
                    }
                    setDraftMessages(prev => [...prev, questionMessage]);
                } else {
                    // nextQuestion is null — no visible question remains (either list is empty
                    // or all remaining questions were skipped) — editing is complete.
                    setIsEditingComplete(true);
                }
            } else {
                setIsEditingComplete(true);
            }
        } else {

            if (dependentQuestions.length > 0) {
                const dependentQuestionIds = dependentQuestions.map(q => q.questionId);
                setQuestionsToReAnswerList(dependentQuestionIds);
                setIsEditingComplete(false);

                // Add dependent questions to removal list (avoiding duplicates)
                setQuestionsToRemoveFromOriginal(prev => {
                    const uniqueIds = new Set([...prev, ...dependentQuestionIds]);
                    return Array.from(uniqueIds);
                });

                // Show the first dependent question
                let firstQuestion = dependentQuestions[0];
                // Enrich with pricing rules from original if available
                firstQuestion = enrichQuestionWithPricingRules(firstQuestion);
                const currentFormValues = getFormValuesFromMessages([
                    ...(editingItem?.messages ?? []),
                    ...(freshMessages ?? draftMessages),
                ]);
                const filteredQuestion = filterQuestionOptions(firstQuestion, currentAnswers, currentProduct, currentFormValues);

                // Pre-populate with existing answer from original item if available
                const existingUserMsg = editingItem?.messages.find(
                    msg => msg.type === 'user' && msg.question?.questionId === firstQuestion.questionId
                );

                const questionMessage: IAssistantMessage = {
                    id: `edit-bot-${Date.now()}-${firstQuestion.questionId}`,
                    type: 'assistant',
                    message: filteredQuestion.question,
                    question: filteredQuestion,
                    response: existingUserMsg?.response ?? { answer: [], selectedOptionsIds: [], form: {} }
                };

                // Inject hardware skip alert if hardware was skipped between current and first dependent question
                const hardwareRequiredYes_2 = Object.values(currentAnswers).some(opts =>
                    opts.some(o => o.optionType === OPTION_TYPE_HARDWARE_REQUIRED && o.responseValue === 'yes')
                );
                const allHardwareTypeQs_2 = questionFlow.filter(q => q.optionType === OPTION_TYPE_HARDWARE_TYPE);
                const anyHardwareTypeVisible_2 = allHardwareTypeQs_2.some(q =>
                    shouldShowQuestion(q, currentAnswers, currentProduct, false, currentFormValues)
                );
                if (!hardwareRequiredYes_2 && !anyHardwareTypeVisible_2 && allHardwareTypeQs_2.length > 0) {
                    const qIds = questionFlow.map(q => q.questionId);
                    const curIdx = qIds.indexOf(questionId);
                    const nxtIdx = qIds.indexOf(firstQuestion.questionId);
                    const hwIdx = qIds.indexOf(allHardwareTypeQs_2[0].questionId);
                    const hardwarePassedHere = hwIdx >= 0 &&
                        (curIdx < 0 || hwIdx > curIdx) &&
                        hwIdx < nxtIdx;
                    if (hardwarePassedHere) {
                        const hardwareRequiredNo = Object.values(currentAnswers).some(opts =>
                            opts.some(o => o.optionType === OPTION_TYPE_HARDWARE_REQUIRED && o.responseValue === 'no')
                        );
                        const bothSashesFixed = Object.values(currentAnswers).some(opts =>
                            opts.some(o => o.optionType === OPTION_TYPE_SASH && o.responseValue === 'both_sashes_fixed')
                        );
                        const skipMessage = hardwareRequiredNo
                            ? 'You selected No for Hardware Required. As a result, all hardware questions have been skipped.'
                            : bothSashesFixed
                            ? 'You have selected Both Sashes Fixed for all frames. As a result, no hardware is required and the hardware selection has been skipped.'
                            : 'Hardware is not applicable for this configuration and has been skipped.';
                        setDraftMessages(prev => {
                            if (prev.some(m => m.id?.startsWith('hardware_skip_alert_'))) return prev;
                            return [...prev, {
                                id: `hardware_skip_alert_${Date.now()}`,
                                type: 'alert' as const,
                                alertVariant: 'info' as const,
                                alertTitle: 'No Hardware Required',
                                message: skipMessage,
                                question: {} as any,
                                response: { answer: [], selectedOptionsIds: [], form: {} },
                            }];
                        });
                    }
                }

                injectTrickleVentSkipAlerts(currentAnswers, currentProduct, questionId, firstQuestion.questionId);
                injectSpraySkipAlert(currentProduct, questionId, firstQuestion.questionId);
                if (firstQuestion.optionType === OPTION_TYPE_HARDWARE_REQUIRED) {
                    setDraftMessages(prev => {
                        if (prev.some(m => m.id === 'hardware_required_alert')) return prev;
                        return [...prev, {
                            id: 'hardware_required_alert',
                            type: 'alert' as const,
                            alertVariant: 'info' as const,
                            alertTitle: 'Hardware on Fixed Sash',
                            message: 'This window has a fixed sash. Hardware is not normally required for fixed sash windows, but you can choose to add it if needed.',
                            question: {} as any,
                            response: { answer: [], selectedOptionsIds: [], form: {} },
                        }];
                    });
                }
                setDraftMessages(prev => [...prev, questionMessage]);
            } else {
                setQuestionsToReAnswerList([]);
                setIsEditingComplete(true);
            }
        }
    };

    const resetWindowVariationPricesToBase = (msgs: IAssistantMessage[]): IAssistantMessage[] => {
        const flowOptionMap = new Map(
            questionFlow.flatMap(q => q.options ?? []).map(o => [o.optionId, o])
        );
        const isWindowOrVariation = (optionType: number) =>
            optionType === OPTION_TYPE_WINDOW_TYPE || optionType === OPTION_TYPE_VARIATION;
        const resetOption = (a: IQuestionOption) => {
            if (!isWindowOrVariation(a.optionType)) return a;
            const flowOpt = flowOptionMap.get(a.optionId);
            if (!flowOpt) return a;
            return { ...a, price: flowOpt.price, priceModifier: flowOpt.priceModifier };
        };
        return msgs.map(m => {
            const hasWindowAnswer = m.response?.answer?.some(a => isWindowOrVariation(a.optionType));
            const hasWindowOption = m.question?.options?.some(o => isWindowOrVariation(o.optionType));
            if (!hasWindowAnswer && !hasWindowOption) return m;
            return {
                ...m,
                question: hasWindowOption
                    ? { ...m.question, options: m.question.options?.map(resetOption) }
                    : m.question,
                response: hasWindowAnswer
                    ? { ...m.response, answer: m.response.answer.map(resetOption) }
                    : m.response,
            };
        });
    };

    // ========================================================================
    // PRICING SURGE HELPER
    // ========================================================================

    const addPricingSurgeIfNeeded = (
        question: IQuestion,
        answers: IQuestionOption[] | Obj,
        updatedMessages: IAssistantMessage[]
    ): void => {
        let currentAnswerOptions: IQuestionOption[] = [];
        if (Array.isArray(answers)) {
            currentAnswerOptions = answers;
        } else {
            Object.entries(answers).forEach(([key]) => {
                const option = question.options?.find(opt =>
                    opt.responseValue === key || opt.optionId === key
                );
                if (option) currentAnswerOptions.push(option);
            });
        }

        const pricingRules: any[] = [];
        if (question.priceConditions?.length) pricingRules.push(...question.priceConditions);
        currentAnswerOptions.forEach(opt => {
            if (opt.priceConditions?.length) pricingRules.push(...opt.priceConditions);
        });
        if (pricingRules.length === 0) return;

        // Enrich stored rules with fresh discount info from the live questionFlow —
        // rules embedded on a reconstructed/edited question can be missing their
        // discountPercentage/discountAmount (merged in from pricingRuleDiscounts at
        // config-load time onto questionFlow, but not always carried through onto
        // every stored copy of the rule), which silently doubles the customer-facing
        // surge amount since no discount gets subtracted before margin. Mirrors the
        // same enrichRule fix already applied in evaluateAllSurgePricesFromMessages.
        const liveRuleDiscountMap = new Map<string, { discountPercentage?: number; discountAmount?: number }>();
        (questionFlow ?? []).forEach((q: any) => {
            const collect = (pc: any) => {
                if ((pc.discountPercentage ?? 0) > 0 || (pc.discountAmount ?? 0) > 0) {
                    liveRuleDiscountMap.set(pc.dogma_pricingruleid, {
                        discountPercentage: pc.discountPercentage,
                        discountAmount: pc.discountAmount,
                    });
                }
            };
            (q.priceConditions ?? []).forEach(collect);
            (q.options ?? []).forEach((o: any) => (o.priceConditions ?? []).forEach(collect));
        });
        const enrichRule = (rule: any): any => {
            if ((rule.discountPercentage ?? 0) > 0 || (rule.discountAmount ?? 0) > 0) return rule;
            const d = liveRuleDiscountMap.get(rule.dogma_pricingruleid);
            return d ? { ...rule, ...d } : rule;
        };
        const enrichedRules = pricingRules.map(enrichRule);

        const allSurgeItems: any[] = [];
        const allAnswers = getAnswersExcludingRemoved(updatedMessages);
        const margin = marginPercentage ?? 0;

        if (Array.isArray(answers)) {
            answers.forEach(opt => {
                const surgeItems = evaluateValueBasedRules(
                    question, opt.responseValue || opt.optionText, opt,
                    enrichedRules, allAnswers, questionFlow, updatedMessages, formatPrice, margin
                );
                allSurgeItems.push(...surgeItems);
            });
        } else {
            const hasFrameWidths = Object.keys(answers).some(k => /^window_\d+_width_/i.test(k));
            Object.entries(answers).forEach(([key, value]) => {
                if (key.startsWith('__')) return;
                if (hasFrameWidths && /^(overall_width|width)_\(mm\)$/.test(key)) return;
                const opt = question.options?.find(o => o.responseValue === key || o.optionId === key)
                    ?? questionFlow.find(q => q.questionId === question.questionId)
                        ?.options?.find((o: any) => o.responseValue === key || o.optionId === key);
                if (opt || key === 'textValue') {
                    const surgeItems = evaluateValueBasedRules(
                        question, value as string | number, opt,
                        enrichedRules, allAnswers, questionFlow, updatedMessages, formatPrice, margin
                    );
                    allSurgeItems.push(...surgeItems);
                }
            });
        }

        const optionBasedItems = evaluateOptionBasedRules(
            currentAnswerOptions, allAnswers, enrichedRules, questionFlow, updatedMessages, formatPrice, margin, question
        );
        allSurgeItems.push(...optionBasedItems);

        let uniqueSurgeItems = allSurgeItems.filter((item, index, self) =>
            index === self.findIndex(t =>
                t.ruleId === item.ruleId &&
                (t.triggerOption?.optionId || '') === (item.triggerOption?.optionId || '')
            )
        );

        // Foil surcharge is a one-off per quote — suppress it if another cart item already owns it,
        // but keep it if the item being edited was the original owner.
        const thisItemHadFoilCharge = (editingItem?.messages ?? []).some(m =>
            m.type === MESSAGE_TYPE_ENUM.priceSurge &&
            (m.surgeItems ?? []).some((s: ISurgeItem) => s.resolverValue === FOIL_SURCHARGE_RESOLVER)
        );
        if (!thisItemHadFoilCharge) {
            const otherItems = (cart?.windowConfiguration ?? []).filter(w => w.id !== editingItemId);
            const foilOwnedByOther = otherItems.some(w =>
                w.messages.some(m =>
                    m.type === MESSAGE_TYPE_ENUM.priceSurge &&
                    (m.surgeItems ?? []).some((s: ISurgeItem) => s.resolverValue === FOIL_SURCHARGE_RESOLVER)
                )
            );
            if (foilOwnedByOther) {
                uniqueSurgeItems = uniqueSurgeItems.filter(s => s.resolverValue !== FOIL_SURCHARGE_RESOLVER);
            }
        }

        if (uniqueSurgeItems.length === 0) return;

        let responseData: { answer: IQuestionOption[]; selectedOptionsIds: string[]; form: Obj };
        if (Array.isArray(answers)) {
            responseData = {
                answer: answers,
                selectedOptionsIds: answers.map(opt => opt.optionId),
                form: {}
            };
        } else {
            const formAsOptions: IQuestionOption[] = Object.entries(answers).map(([key, value]) => {
                const opt = question.options?.find(o => o.responseValue === key || o.optionId === key);
                return opt || {
                    optionId: key, vssPrice: 0, optionText: String(value), responseValue: String(value),
                    description: '', numberOfFrames: 0, optionType: 0, inputType: 0,
                    optionTypeName: '', order: 0, price: 0, priceModifier: 0, thumbnail: ''
                };
            });
            responseData = {
                answer: formAsOptions,
                selectedOptionsIds: formAsOptions.map(opt => opt.optionId),
                form: answers as Obj
            };
        }

        // Remove existing surge messages with the same resolverValue only when triggered
        // by the SAME question (same responsePath). Surges from other questions are kept
        // so that updating one frame's sash does not wipe another question's surge.
        const newResolverValues = new Set(uniqueSurgeItems.map(s => s.resolverValue).filter(Boolean));
        const currentResponsPath = question.responsePath;
        const filtered = updatedMessages.filter(m => {
            if (m.type !== MESSAGE_TYPE_ENUM.priceSurge) return true;
            if (currentResponsPath) {
                const existingResponsPath = m.question?.responsePath;
                if (existingResponsPath && existingResponsPath !== currentResponsPath) return true;
            }
            const existingResolvers = (m.surgeItems ?? []).map((s: ISurgeItem) => s.resolverValue);
            return !existingResolvers.some(r => newResolverValues.has(r));
        });
        updatedMessages.splice(0, updatedMessages.length, ...filtered);

        updatedMessages.push({
            id: `surge_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
            type: 'price-surge',
            message: formatSurgeMessage(uniqueSurgeItems, formatPrice),
            question,
            response: responseData,
            surgeItems: uniqueSurgeItems
        });
    };

    const handleSingleSelect = (questionId: string, option: IQuestionOption) => {
        // Step 1: Find the bot message and check if it's an update
        const botMessageIndex = draftMessages.findIndex(
            msg => msg.type === 'assistant' && msg.question.questionId === questionId
        );
        if (botMessageIndex === -1) {
            return;
        }

        const botMessage = draftMessages[botMessageIndex];
        // Use question from bot message (preserves pricing rules from original)
        const question = botMessage.question;
        const isUpdate = botMessage.response?.answer && botMessage.response.answer.length > 0;

        // Step 2: Update bot message response and handle messages
        let updatedMessages = [...draftMessages];

        if (isUpdate) {
            // This is an update - remove all messages after this question
            updatedMessages = updatedMessages.slice(0, botMessageIndex + 1);
        }

        // Update bot message with user's response
        updatedMessages[botMessageIndex] = {
            ...botMessage,
            response: {
                answer: [option],
                selectedOptionsIds: [option.optionId],
                form: {}
            }
        };

        // Add user message
        const userMessage = createUserResponseMessage(question, [option]);
        updatedMessages.push(userMessage);

        // When window type, variation, or security is re-answered, reset all stored
        // window type / variation prices (displayed cards + stored answers) back to
        // base config values so the glazing alert comparison works correctly.
        if ([OPTION_TYPE_WINDOW_TYPE, OPTION_TYPE_VARIATION, OPTION_TYPE_SECURITY].includes(
            question.optionType ?? -1
        )) {
            updatedMessages = resetWindowVariationPricesToBase(updatedMessages);
        }

        // Glazing type selected: resolve new base price for window type / variation.
        // Apply the resolved prices back into draft messages (for window/variation
        // messages re-answered during this edit session), and show an alert when
        // the base price changes. Save-time applyGlazingPriceToMessages ensures the
        // final saved price is always correct for original-cart messages too.
        if (question.optionType === OPTION_TYPE_GLAZING_TYPE) {
            // Reset original saved messages' window/variation prices to base so oldPrice
            // comparison starts from the true base price (saved messages have glazing-adjusted prices).
            const originalMsgs = resetWindowVariationPricesToBase(
                (editingItem?.messages ?? []).filter(m => !m.id?.startsWith('glazing_price_alert_'))
            );
            const draftMsgsClean = updatedMessages.filter(m => !m.id?.startsWith('glazing_price_alert_'));
            const merged = [...originalMsgs, ...draftMsgsClean];

            const findBaseMsg = (msgs: typeof merged) =>
                msgs.find(m => m.type === 'user' && m.response?.answer?.[0]?.optionType === OPTION_TYPE_VARIATION) ??
                msgs.find(m => m.type === 'user' && m.response?.answer?.[0]?.optionType === OPTION_TYPE_WINDOW_TYPE);
            const baseMsg = findBaseMsg(merged);
            const oldPrice = baseMsg?.response?.answer?.[0]?.price;

            // Use getMergedAnswers so the new glazing answer overrides the old one
            // (getAnswersFromMessages on merged would append both, causing wrong rule match).
            const mergedAnswers = getMergedAnswers(originalMsgs, draftMsgsClean);
            const resolved = applyGlazingPriceToMessages(merged, questionFlow, mergedAnswers);

            // Write resolved prices back into any draft messages that contain
            // window type / variation answers (e.g. user changed window type in
            // this editing session and then changed glazing).
            const resolvedById = new Map(resolved.map(m => [m.id, m]));
            updatedMessages = updatedMessages.map(m => resolvedById.get(m.id) ?? m);

            const updatedBaseMsg = findBaseMsg(resolved);
            const newPrice = updatedBaseMsg?.response?.answer?.[0]?.price;
            const productLabel = updatedBaseMsg?.response?.answer?.[0]?.optionText;
            if (newPrice && newPrice !== oldPrice) {
                updatedMessages.push({
                    id: `glazing_price_alert_${Date.now()}`,
                    type: 'alert' as const,
                    alertVariant: 'info' as const,
                    alertTitle: 'Base Price Updated',
                    message: `The base price for ${productLabel ?? 'your window'} has been updated to ${formatPrice(newPrice)} based on your glazing selection.`,
                    question: {} as any,
                    response: { answer: [], selectedOptionsIds: [], form: {} },
                });
            }
        }

        addPricingSurgeIfNeeded(question, [option], updatedMessages);
        setDraftMessages(updatedMessages);

        // Step 2: Update selectedProduct if this option is a product
        let currentProduct = selectedProduct;
        let isChanged = false;
        if (option.optionType === OPTION_TYPE_VARIATION || option.optionType === OPTION_TYPE_WINDOW_TYPE) {
            const product = getProductFromOption(option);
            if (product) {
                if (currentProduct?.optionId !== product.optionId) {
                    isChanged = true

                }
                currentProduct = option;
                setSelectedProduct(option);
                setIsEnableBlend(true)
            } else {
                setSelectedProduct(undefined)
                currentProduct = undefined;
            }
        }

        // Enable blending when a color option is changed (uses existing product thumbnail)
        if (EXTERNAL_COLOR_OPTIONS.includes(option.optionType) || option.optionType === PROFILE_COLOR_OPTION) {
            if (selectedProduct || editedItemProduct) {
                setIsEnableBlend(true);
            }
        }

        // Enable blending when a grill option is changed — segment replace runs after grill settles
        const grillOptionValues = Object.values(GRILL_OPTION_TYPES) as number[];
        if (grillOptionValues.includes(option.optionType)) {
            if (selectedProduct || editedItemProduct) {
                setIsEnableBlend(true);
            }
        }

        // Enable blending when Deep Bottom Rail is re-selected (optionType 2)
        if (option.optionType === 2) {
            if (selectedProduct || editedItemProduct) {
                setIsEnableBlend(true);
            }
        }

        // Enable blending when parent Decorative Bar Type (63) changes — including None
        if (option.optionType === 63) {
            if (selectedProduct || editedItemProduct) {
                setIsEnableBlend(true);
            }
        }                const isProductChanged = isChanged || !currentProduct;

        // When the selected product changes (different numberOfFrames / numberOfSashes),
        // rebake all already-answered messages so per-sash / per-frame prices (hardware,
        // spray, add-ons, etc.) reflect the new product's multipliers.  These questions
        // are not re-asked as dependents, so without rebaking their prices stay stale.
        if (isProductChanged && [OPTION_TYPE_WINDOW_TYPE, OPTION_TYPE_VARIATION].includes(
            question.optionType ?? -1
        )) {
            updatedMessages = rebakeMessagesWithLatestState(updatedMessages, questionFlow, selectedProduct as IQuestionOption);

            // Coupling clips are only valid for Bay and Coupled windows.
            // If the new product is Standard or Arch, remove any stale clip message immediately.
            const newProductType = currentProduct?.producttypecode;
            const newProductSupportsCouplingClip =
                newProductType === ProductStructureType.Bays ||
                newProductType === ProductStructureType.Coupled;
            if (!newProductSupportsCouplingClip) {
                updatedMessages = updatedMessages.filter(m => m.id !== COUPLING_CLIP_MSG_ID);
            }

            setDraftMessages(updatedMessages);
        }

        // Step 3: Build answers by merging original messages with updated draft on a
        // per-question basis so that sibling questions sharing the same category are
        // preserved when one question in that category is re-answered.
        const currentAnswers = getAnswersExcludingRemoved(updatedMessages);
        // May be narrowed below when sash changes to non-fixed and strips stale hardware_required answer
        let answersForDependents = currentAnswers;
        let dependentQuestions = getNextVisibleDependentQuestion(
            questionId,
            questionFlow,
            {
                [question.category]: [option]
            },
            isProductChanged,
            currentProduct
        );
        // Step 3.5: When product changes, find and sort all related questions (dependent + parent questions)
        if (isProductChanged && dependentQuestions.length > 0) {
            dependentQuestions = findAndSortQuestionsForProductChange(
                dependentQuestions,
                questionFlow,
                isProductChanged
            );

            logQuestionHierarchy(dependentQuestions);
        }

        // Sash re-answered: strip any stale hardware skip alert.
        // If the new sash is non-fixed and the original cart had Hardware Required = No
        // (set because sashes were fixed), remove that stale answer from the answers
        // context so hardware questions are re-enabled, and force the hardware_required
        // question to be re-asked.
        if (question.optionType === OPTION_TYPE_SASH) {
            updatedMessages = updatedMessages.filter(m => !m.id?.startsWith('hardware_skip_alert_'));
            setDraftMessages([...updatedMessages]);

            if (option.responseValue !== BOTH_SASHES_FIXED_VALUE) {
                const staleHardwareNo = Object.values(currentAnswers).some(opts =>
                    opts.some(o => o.optionType === OPTION_TYPE_HARDWARE_REQUIRED && o.responseValue === 'no')
                );
                if (staleHardwareNo) {
                    const hwRequiredQ = questionFlow.find(q => q.optionType === OPTION_TYPE_HARDWARE_REQUIRED);
                    if (hwRequiredQ) {
                        // Remove stale "No" from the answers context passed to handleDependentQuestions
                        const { [hwRequiredQ.category]: _stripped, ...rest } = answersForDependents;
                        answersForDependents = rest;
                        // Force hardware_required to be re-asked and its old answer removed on save
                        setQuestionsToRemoveFromOriginal(prev =>
                            prev.includes(hwRequiredQ.questionId) ? prev : [...prev, hwRequiredQ.questionId]
                        );
                        if (!dependentQuestions.some(q => q.questionId === hwRequiredQ.questionId)) {
                            dependentQuestions = [...dependentQuestions, hwRequiredQ];
                        }
                    }
                }
            }
        }

        // Hardware Required (Q0156) re-answered: strip both the pre-question alert and
        // any prior hardware skip alert for a clean state before re-injection.
        if (question.optionType === OPTION_TYPE_HARDWARE_REQUIRED) {
            updatedMessages = updatedMessages.filter(
                m => m.id !== 'hardware_required_alert' && !m.id?.startsWith('hardware_skip_alert_')
            );
            setDraftMessages([...updatedMessages]);
        }

        // Cill selection: inject an info alert so the user knows the cill colour
        // will match their profile colour and how to request a different one.
        // Strip any previous cill alert first so it re-appears at the correct position.
        if (question.optionType === OPTION_TYPE_CILL) {
            updatedMessages = updatedMessages.filter(m => !m.id?.startsWith('cill_colour_alert_'));
            {
                const cillAlert = {
                    id: `cill_colour_alert_${Date.now()}`,
                    type: 'alert' as const,
                    alertVariant: 'info' as const,
                    alertTitle: 'Cill Colour Information',
                    message:
                        'Please note that the cill colour will be supplied to match your selected profile colour. ' +
                        'If you require the cill in a different colour, please contact your administrator for further assistance.',
                    question: {} as any,
                    response: { answer: [], selectedOptionsIds: [], form: {} },
                };
                updatedMessages.push(cillAlert);
                setDraftMessages([...updatedMessages]);
            }
        }

        // Step 4: Handle dependent questions using common logic
        handleDependentQuestions(questionId, dependentQuestions, answersForDependents, currentProduct, isUpdate);
    };

    const handleMultipleChoice = (questionId: string, options: IQuestionOption[]) => {

        // Step 1: Find the bot message and check if it's an update
        const botMessageIndex = draftMessages.findIndex(
            msg => msg.type === 'assistant' && msg.question.questionId === questionId
        );

        if (botMessageIndex === -1) {
            return;
        }

        const botMessage = draftMessages[botMessageIndex];
        // Use question from bot message (preserves pricing rules from original)
        const question = botMessage.question;
        const isUpdate = botMessage.response?.answer && botMessage.response.answer.length > 0;

        // Step 2: Update bot message response and handle messages
        let updatedMessages = [...draftMessages];

        if (isUpdate) {
            // This is an update - remove all messages after this question
            updatedMessages = updatedMessages.slice(0, botMessageIndex + 1);
        }

        // Update bot message with user's response
        updatedMessages[botMessageIndex] = {
            ...botMessage,
            response: {
                answer: options,
                selectedOptionsIds: options.map(opt => opt.optionId),
                form: {}
            }
        };

        // Add user message
        const userMessage = createUserResponseMessage(question, options);
        updatedMessages.push(userMessage);

        // When window type, variation, or security is re-answered, reset all stored
        // window type / variation prices (displayed cards + stored answers) back to
        // base config values so the glazing alert comparison works correctly.
        if ([OPTION_TYPE_WINDOW_TYPE, OPTION_TYPE_VARIATION, OPTION_TYPE_SECURITY].includes(
            question.optionType ?? -1
        )) {
            updatedMessages = resetWindowVariationPricesToBase(updatedMessages);
        }

        // Angled Cill Horn: update bay_poles (±2) and inject/remove info alert.
        if (question.optionType === OPTION_TYPE_ANGLED_CILL_HORN) {
            const isYes = options[0]?.responseValue === 'yes';
            const wasYes = botMessage.response?.answer?.[0]?.responseValue === 'yes';

            if (isYes !== wasYes) {
                const allMsgsForDim = [...(editingItem?.messages ?? []), ...updatedMessages];
                const reversedDimIdx = [...allMsgsForDim].reverse().findIndex(
                    m => m.type === 'user' && m.question?.optionType === DIMENSION_OPTION
                );
                if (reversedDimIdx !== -1) {
                    const dimMsg = allMsgsForDim[allMsgsForDim.length - 1 - reversedDimIdx];
                    const currentBayPoles = Number(dimMsg.response?.form?.bay_poles) || BAY_POLES_DEFAULT;
                    const newBayPoles = isYes
                        ? currentBayPoles + 2
                        : Math.max(BAY_POLES_DEFAULT, currentBayPoles - 2);
                    const updatedDimForm = { ...dimMsg.response?.form, bay_poles: newBayPoles };
                    const updatedDimMsg = { ...dimMsg, response: { ...dimMsg.response, form: updatedDimForm } };
                    const existingDraftIdx = updatedMessages.findIndex(m => m.id === dimMsg.id);
                    if (existingDraftIdx !== -1) {
                        updatedMessages[existingDraftIdx] = updatedDimMsg;
                    } else {
                        updatedMessages.unshift(updatedDimMsg);
                    }
                }
            }

            const alertId = 'angled_cill_bay_poles_alert';
            const withoutOldAlert = updatedMessages.filter(m => m.id !== alertId);
            if (isYes) {
                withoutOldAlert.push({
                    id: alertId,
                    type: 'alert' as const,
                    alertVariant: 'info' as const,
                    alertTitle: 'Bay Poles Updated',
                    message: '2 additional bay poles have been added to your configuration.',
                    question: {} as any,
                    response: { answer: [], selectedOptionsIds: [], form: {} },
                });
            }
            updatedMessages.splice(0, updatedMessages.length, ...withoutOldAlert);
        }

        addPricingSurgeIfNeeded(question, options, updatedMessages);
        setDraftMessages(updatedMessages);

        // Step 2: Update selectedProduct if any option is a product
        let currentProduct = selectedProduct;
        for (const option of options) {
            if (option.optionType === OPTION_TYPE_VARIATION || option.optionType === OPTION_TYPE_WINDOW_TYPE) {
                const product = getProductFromOption(option);
                if (product) {
                    currentProduct = option;
                    setSelectedProduct(option);
                    setIsEnableBlend(true);
                    break;
                }
            }
        }

        // Step 3: Get current answers and find dependent questions
        const currentAnswers = getAnswersExcludingRemoved(updatedMessages);
        const dependentQuestions = getNextVisibleDependentQuestion(
            questionId,
            questionFlow,
            {
                [question.category]: options
            },
            Boolean(editedItemProduct?.optionId !== currentProduct?.optionId),
            currentProduct
        );

        // Step 4: Handle dependent questions using common logic
        handleDependentQuestions(questionId, dependentQuestions, currentAnswers, currentProduct, isUpdate);
    };

    const handleFormSubmit = (questionId: string, formValues: Obj) => {

        // Step 1: Find the bot message and check if it's an update
        const botMessageIndex = draftMessages.findIndex(
            msg => msg.type === 'assistant' && msg.question.questionId === questionId
        );

        if (botMessageIndex === -1) {
            return;
        }

        const botMessage = draftMessages[botMessageIndex];
        // Use question from bot message (preserves pricing rules from original)
        const question = botMessage.question;
        const isUpdate = botMessage.response?.form && Object.keys(botMessage.response.form).length > 0;

        // Bay window dimensions: recalculate bay poles and coupling clips from angles.
        let bayWindowCouplingClips = -1; // -1 = not a bay dimension form
        let bayWindowBayPoles = 0;
        const product = selectedProduct ?? editedItemProduct;
        if (
            question.optionType === DIMENSION_OPTION &&
            product?.producttypecode === ProductStructureType.Bays
        ) {
            const allMsgs = [...(editingItem?.messages ?? []), ...draftMessages];
            const isAngledSillHornsYes = allMsgs.some(
                m => m.type === 'user' &&
                    m.question?.optionType === OPTION_TYPE_ANGLED_CILL_HORN &&
                    m.response?.answer?.[0]?.responseValue === 'yes'
            );
            const { bayPoles, couplingClips, hasAngles } = calculateBayPolesAndCouplingClips(
                formValues as Record<string, unknown>,
                isAngledSillHornsYes,
            );
            if (hasAngles) {
                formValues = { ...formValues, bay_poles: bayPoles };
                bayWindowCouplingClips = couplingClips;
                bayWindowBayPoles = bayPoles;
            }
        }

        // Couple window dimensions: inject coupling clips based on frame count (frames - 1).
        let coupleWindowCouplingClips = -1;
        if (
            question.optionType === DIMENSION_OPTION &&
            product?.producttypecode === ProductStructureType.Coupled
        ) {
            const frameCount = product.numberOfFrames ?? 0;
            if (frameCount >= 2) {
                coupleWindowCouplingClips = frameCount - 1;
            }
        }

        // Step 2: Update bot message response and handle messages
        let updatedMessages = [...draftMessages];

        if (isUpdate) {
            // This is an update - remove all messages after this question
            updatedMessages = updatedMessages.slice(0, botMessageIndex + 1);
        }

        // Convert form values to IQuestionOption format for consistency.
        // Skip metadata keys (e.g. __split_mode__) — those live in response.form
        // for UI-state restoration only and shouldn't appear in response.answer.
        const formAsOptions: IQuestionOption[] = Object.entries(formValues)
            .filter(([key]) => !key.startsWith('__'))
            .map(([key, value]) => {
                const option = question.options?.find(opt =>
                    opt.responseValue === key || opt.optionId === key
                );
                return option || {
                    optionId: key,
                    optionText: String(value),
                    responseValue: key,
                    description: '',
                    numberOfFrames: 0,
                    vssPrice: 0,
                    optionType: 0,
                    inputType: 0,
                    optionTypeName: '',
                    order: 0,
                    price: 0,
                    priceModifier: 0,
                    thumbnail: ''
                };
            });

        // Update bot message with user's response
        updatedMessages[botMessageIndex] = {
            ...botMessage,
            response: {
                answer: formAsOptions,
                selectedOptionsIds: formAsOptions.map(opt => opt.optionId),
                form: formValues
            }
        };

        // Add user message
        const userMessage = createUserResponseMessage(question, formAsOptions, formValues);
        updatedMessages.push(userMessage);

        // Bay window: strip stale coupling clip message and inject a fresh one.
        if (bayWindowCouplingClips >= 0) {
            const stripped = updatedMessages.filter(m => m.id !== COUPLING_CLIP_MSG_ID);
            const clipMsg = buildCouplingClipMessage(bayWindowCouplingClips, questionFlow);
            if (clipMsg) stripped.push(clipMsg);
            updatedMessages.splice(0, updatedMessages.length, ...stripped);
        }

        // Couple window: strip stale coupling clip message and inject a fresh one.
        if (coupleWindowCouplingClips >= 0) {
            const stripped = updatedMessages.filter(m => m.id !== COUPLING_CLIP_MSG_ID);
            const clipMsg = buildCouplingClipMessage(coupleWindowCouplingClips, questionFlow);
            if (clipMsg) stripped.push(clipMsg);
            updatedMessages.splice(0, updatedMessages.length, ...stripped);
        }

        // Dimension info alert: inform the user how many bay poles / coupling clips were added.
        if (bayWindowCouplingClips >= 0) {
            const poles = bayWindowBayPoles;
            const clips = bayWindowCouplingClips;
            const polesPart = `${poles} bay pole${poles !== 1 ? 's' : ''}`;
            const clipsPart = clips > 0 ? ` and ${clips} coupling clip pair${clips !== 1 ? 's' : ''}` : '';
            const withAlert = updatedMessages.filter(m => m.id !== DIMENSION_ALERT_ID);
            withAlert.push({
                id: DIMENSION_ALERT_ID,
                type: 'alert' as const,
                alertVariant: 'info' as const,
                alertTitle: 'Configuration Updated',
                message: `${polesPart}${clipsPart} have been calculated from your angles and added to your configuration.`,
                question: {} as any,
                response: { answer: [], selectedOptionsIds: [], form: {} },
            });
            updatedMessages.splice(0, updatedMessages.length, ...withAlert);
        } else if (coupleWindowCouplingClips >= 0) {
            const clips = coupleWindowCouplingClips;
            const withAlert = updatedMessages.filter(m => m.id !== DIMENSION_ALERT_ID);
            withAlert.push({
                id: DIMENSION_ALERT_ID,
                type: 'alert' as const,
                alertVariant: 'info' as const,
                alertTitle: 'Configuration Updated',
                message: `${clips} coupling clip pair${clips !== 1 ? 's' : ''} have been added to your coupled window configuration.`,
                question: {} as any,
                response: { answer: [], selectedOptionsIds: [], form: {} },
            });
            updatedMessages.splice(0, updatedMessages.length, ...withAlert);
        }

        // Spray colour alert: show when inside and outside spray colours differ.
        // When re-answering one colour, the other may have been sliced from updatedMessages
        // (isUpdate removes downstream messages), so fall back to editingItem?.messages.
        if (
            question.optionType === OPTION_TYPE_INSIDE_SPRAY_COLOUR ||
            question.optionType === OPTION_TYPE_OUTSIDE_SPRAY_COLOUR
        ) {
            // When re-answering inside colour, the isUpdate slice removes downstream
            // messages so fall back to editingItem?.messages for the other colour.
            // Always use the LAST match so re-answers pick up the freshest value.
            const lastMsg = (arr: IAssistantMessage[], optType: number) => {
                const matches = arr.filter(m => m.type === 'user' && m.question?.optionType === optType);
                return matches[matches.length - 1] ?? null;
            };
            const insideUserMsg =
                lastMsg(updatedMessages, OPTION_TYPE_INSIDE_SPRAY_COLOUR) ??
                lastMsg(editingItem?.messages ?? [], OPTION_TYPE_INSIDE_SPRAY_COLOUR);
            const outsideUserMsg =
                lastMsg(updatedMessages, OPTION_TYPE_OUTSIDE_SPRAY_COLOUR) ??
                lastMsg(editingItem?.messages ?? [], OPTION_TYPE_OUTSIDE_SPRAY_COLOUR);
            const withoutSprayAlert = updatedMessages.filter(m => m.id !== SPRAY_COLOUR_ALERT_ID);
            if (insideUserMsg && outsideUserMsg) {
                const insideForm = insideUserMsg.response?.form ?? {};
                const outsideForm = outsideUserMsg.response?.form ?? {};
                const insideRal = String(insideForm['internal_ral_code'] ?? '').trim();
                const outsideRal = String(outsideForm['external_ral_code'] ?? '').trim();
                const insideFb = String(insideForm['internal_farrow_&_ball'] ?? '').trim();
                const outsideFb = String(outsideForm['external_farrow_&_ball'] ?? '').trim();
                const insideColour = insideRal || insideFb;
                const outsideColour = outsideRal || outsideFb;
                if (insideColour && outsideColour && insideColour.toLowerCase() !== outsideColour.toLowerCase()) {
                    withoutSprayAlert.push({
                        id: SPRAY_COLOUR_ALERT_ID,
                        type: 'alert' as const,
                        alertVariant: 'info' as const,
                        alertTitle: 'Two-Colour Spray — Price Doubled',
                        message: `The inside spray colour (${insideColour}) and outside spray colour (${outsideColour}) are different. Two separate spray runs are required, so the spray price has been doubled.`,
                        question: {} as any,
                        response: { answer: [], selectedOptionsIds: [], form: {} },
                    });
                }
            }
            updatedMessages.splice(0, updatedMessages.length, ...withoutSprayAlert);
        }

        addPricingSurgeIfNeeded(question, formValues, updatedMessages);
        setDraftMessages(updatedMessages);

        // Step 2: Get current answers and find dependent questions.
        //
        // For dependency detection we restrict the "changed" inputs to only the
        // form fields whose values actually differ from the original submission.
        // Re-submitting the form unchanged (or only touching unrelated fields)
        // should not re-surface downstream questions whose triggering field is
        // unchanged — their existing answers on the cart are still valid.
        const currentProduct = selectedProduct;
        const currentAnswers = getAnswersExcludingRemoved(updatedMessages);

        const originalFormValues = (botMessage.response?.form ?? {}) as Record<string, unknown>;
        const hasOriginal = Object.keys(originalFormValues).length > 0;
        const normalizeFormVal = (v: unknown): string => (v === null || v === undefined ? '' : String(v));
        const changedFormAsOptions = hasOriginal
            ? formAsOptions.filter(opt => {
                const key = opt.responseValue || opt.optionId;
                return normalizeFormVal((formValues as Record<string, unknown>)[key])
                    !== normalizeFormVal(originalFormValues[key]);
            })
            : formAsOptions;

        let dependentQuestions = getNextVisibleDependentQuestion(
            questionId,
            questionFlow,
            {
                [question.category]: changedFormAsOptions
            },
            Boolean(editedItemProduct?.optionId !== currentProduct?.optionId),
            currentProduct
        );

        // Form questions store each filled-in field as an option in response.answer,
        // but the regular dependency lookup matches dep.optionIds against the answer
        // entry's optionId — which may have fallen back to the responseValue string
        // when the form field's GUID wasn't in question.options. Surface any
        // form-field-driven dependents explicitly. Every currently-filled field is
        // always treated as a trigger (no value-change guard) — a presence-based
        // dependent (e.g. Transom Drop) must always be re-surfaced after the form
        // is (re)submitted, whether or not any field's value actually changed.
        const formFieldDependents = getFormFieldDependentQuestions(
            questionId,
            questionFlow,
            formValues as Record<string, unknown>,
        );
        if (formFieldDependents.length) {
            const existingIds = new Set(dependentQuestions.map(q => q.questionId));
            formFieldDependents.forEach(q => {
                if (!existingIds.has(q.questionId)) dependentQuestions.push(q);
            });
        }

        // Special case: arch dimension change may invalidate the existing profile colour selection.
        // - If the stored selection is NO LONGER valid for the new width: ensure it is re-asked.
        // - If the stored selection IS still valid: suppress the re-ask (even if it ended up in
        //   the dependent list via other dependency rules) and keep the original answer intact.
        const isArchProduct = (currentProduct ?? editedItemProduct)?.producttypecode === ProductStructureType.Arch;
        if (question.optionType === DIMENSION_OPTION && isArchProduct) {
            const newWidth = resolveVentWidth(formValues as Record<string, unknown>);
            if (newWidth > 0) {
                const profileColourQ = questionFlow.find(q => q.optionType === PROFILE_COLOR_OPTION);
                if (profileColourQ) {
                    const alreadyInDependents = dependentQuestions.some(q => q.optionType === PROFILE_COLOR_OPTION);
                    const allStoredMsgs = editingItem?.messages ?? [];
                    const profileMsg = allStoredMsgs.find(
                        m => m.type === 'user' && m.question?.optionType === PROFILE_COLOR_OPTION
                    );
                    const existingProfileOption = profileMsg?.response?.answer?.[0];
                    const archProduct = currentProduct ?? editedItemProduct;
                    const isSwept = isSweptArchProduct(archProduct?.optionText);
                    const stillValid = existingProfileOption
                        ? isSwept
                            ? shouldShowSweptArchFoilOption(
                                newWidth,
                                Number((formValues as Record<string, unknown>)['overall_height_(mm)'] ?? 0),
                                Number((formValues as Record<string, unknown>)['shoulder_height_(mm)'] ?? 0),
                            )
                            : shouldShowArchProfileColourOption(existingProfileOption.responseValue ?? '', newWidth)
                        : false; // no existing answer → must re-ask

                    if (stillValid && alreadyInDependents) {
                        // Selection is still valid — remove from dependent list so the user is not re-asked,
                        // and clear it from the removal list so the original answer is preserved on save.
                        dependentQuestions = dependentQuestions.filter(q => q.optionType !== PROFILE_COLOR_OPTION);
                        setQuestionsToRemoveFromOriginal(prev =>
                            prev.filter(id => id !== profileColourQ.questionId)
                        );
                    } else if (!stillValid && !alreadyInDependents) {
                        // Selection is now invalid and not yet queued — inject it.
                        dependentQuestions = [...dependentQuestions, profileColourQ];
                    }
                }
            }
        }

        // When dimensions change, detect questions that need to be re-asked.
        //
        // Three cases:
        //  A. Question-level widthMin/widthMax (e.g. trickle vent questions):
        //     - Width changed AND question is now visible → always re-ask
        //       (pricing and configuration are width-dependent even when the
        //        question was already visible at the old width)
        //     - Was visible, now hidden → add to removal list, don't re-ask
        //  B. Option-level widthMin/widthMax (e.g. trickle vent position):
        //     - Stored option is now hidden → re-ask so user picks a valid one
        //  C. Transom drop / other form-field dependents handled above by
        //     getFormFieldDependentQuestions (dependsOn height/width field optionIds).
        if (question.optionType === DIMENSION_OPTION) {
            const newFV = formValues as Record<string, unknown>;
            const oldFV = originalFormValues;
            const normalize = (v: unknown) => (v === null || v === undefined ? '' : String(v));
            const widthChanged = normalize(newFV['overall_width_(mm)']) !== normalize(oldFV['overall_width_(mm)'])
                || normalize(newFV['width_(mm)']) !== normalize(oldFV['width_(mm)'])
                || [1, 2, 3, 4, 5].some(
                    i => normalize(newFV[`window_${i}_width_(mm)`]) !== normalize(oldFV[`window_${i}_width_(mm)`])
                );
            const existingIds = new Set(dependentQuestions.map(q => q.questionId));
            const allOriginalMsgs = editingItem?.messages ?? [];

            questionFlow.forEach(q => {
                if (existingIds.has(q.questionId)) return;
                if (q.questionId === questionId) return;

                // ── Case A: question-level widthMin / widthMax ─────────────────
                const hasQuestionWidthCond =
                    q.displayCondition?.widthMin !== undefined ||
                    q.displayCondition?.widthMax !== undefined;

                if (hasQuestionWidthCond) {
                    const nowVisible = shouldShowQuestion(q, currentAnswers, currentProduct, true, newFV);

                    if (!nowVisible) {
                        // Question is now hidden — remove its stored answer on save
                        const qId = q.questionId;
                        setQuestionsToRemoveFromOriginal(prev =>
                            prev.includes(qId) ? prev : [...prev, qId]
                        );
                        existingIds.add(qId);
                        return;
                    }

                    if (widthChanged) {
                        // Width changed and question is visible — re-ask it because
                        // trickle vent configuration and pricing depend on the actual width.
                        dependentQuestions = [...dependentQuestions, q];
                        existingIds.add(q.questionId);
                        return;
                    }
                }

                // ── Case B: option-level widthMin / widthMax ───────────────────
                const hasOptionWidthCond = (q.options ?? []).some(
                    o => o.displayCondition?.widthMin !== undefined || o.displayCondition?.widthMax !== undefined
                );
                if (!hasOptionWidthCond) return;

                const storedMsg = allOriginalMsgs.find(
                    m => m.type === 'user' && m.question?.questionId === q.questionId
                );
                if (!storedMsg) return;

                const storedOptions = storedMsg.response?.answer ?? [];
                const visibleOptions = filterQuestionOptions(q, currentAnswers, currentProduct, newFV);
                const visibleIds = new Set((visibleOptions.options ?? []).map(o => o.optionId));
                const hasInvalidOption = storedOptions.some(o => o.optionId && !visibleIds.has(o.optionId));
                if (hasInvalidOption) {
                    dependentQuestions = [...dependentQuestions, q];
                    existingIds.add(q.questionId);
                }
            });

            // ── Case C: hardware item questions ────────────────────────────────
            // Hardware items price varies by frame width (widthQtyThreshold / HARDWARE_WIDTH_QTY_THRESHOLDS).
            // Re-ask the hardware item question when any frame width changed so the
            // user confirms the selection and the new per-frame qty is rendered.
            if (widthChanged) {
                questionFlow.forEach(q => {
                    if (existingIds.has(q.questionId)) return;
                    if (q.optionType !== OPTION_TYPE_HARDWARE_ITEM) return;

                    // Only re-ask if user had already answered it (it exists in stored messages).
                    const wasAnswered = allOriginalMsgs.some(
                        m => m.type === 'user' && m.question?.questionId === q.questionId
                    );
                    if (!wasAnswered) return;

                    dependentQuestions = [...dependentQuestions, q];
                    existingIds.add(q.questionId);
                });
            }
        }

        // Step 3: Handle dependent questions using common logic.
        // Pass updatedMessages so handleDependentQuestions uses fresh form values
        // (draftMessages state is stale at this point — setDraftMessages was just called above).
        handleDependentQuestions(questionId, dependentQuestions, currentAnswers, currentProduct, isUpdate, updatedMessages);
    };

    const handleTextInput = (questionId: string, textValue: string) => {

        // Step 1: Find the bot message and check if it's an update
        const botMessageIndex = draftMessages.findIndex(
            msg => msg.type === 'assistant' && msg.question.questionId === questionId
        );

        if (botMessageIndex === -1) {
            return;
        }

        const botMessage = draftMessages[botMessageIndex];
        // Use question from bot message (preserves pricing rules from original)
        const question = botMessage.question;
        const isUpdate = botMessage.response?.answer && botMessage.response.answer.length > 0;

        // Step 2: Update bot message response and handle messages
        let updatedMessages = [...draftMessages];

        if (isUpdate) {
            // This is an update - remove all messages after this question
            updatedMessages = updatedMessages.slice(0, botMessageIndex + 1);
        }

        // Create a text option for the answer
        const textOption: IQuestionOption = {
            optionId: `text_${Date.now()}`,
            optionText: textValue,
            responseValue: textValue,
            optionType: question.optionType || 0,
            optionTypeName: question.optionTypeName || '',
            description: '',
            numberOfFrames: 0,
            inputType: 0,
            order: 0,
            thumbnail: '',
            vssPrice: 0,
            price: 0,
            priceModifier: 0
        };

        // Update bot message with user's response
        updatedMessages[botMessageIndex] = {
            ...botMessage,
            response: {
                answer: [textOption],
                selectedOptionsIds: [textOption.optionId],
                form: {}
            }
        };

        // Add user message
        const userMessage = createUserResponseMessage(question, [textOption]);
        updatedMessages.push(userMessage);
        addPricingSurgeIfNeeded(question, { textValue }, updatedMessages);
        setDraftMessages(updatedMessages);

        // Step 2: Get current answers and find dependent questions
        const currentProduct = selectedProduct;
        const currentAnswers = getAnswersExcludingRemoved(updatedMessages);
        const dependentQuestions = getNextVisibleDependentQuestion(
            questionId,
            questionFlow,
            {
                [question.category]: [textOption]
            },
            Boolean(editedItemProduct?.optionId !== currentProduct?.optionId),
            currentProduct
        );

        // Step 3: Handle dependent questions using common logic
        handleDependentQuestions(questionId, dependentQuestions, currentAnswers, currentProduct, isUpdate);
    };

    const openDrawer = (itemId: string, item: IWindowCartItem, questionIdToEdit: string, cart: IAssistantCart) => {
        setIsOpen(true);
        setEditingItemId(itemId);
        setEditingItem(item);
        setCart(cart);
        setEditingQuestionId(questionIdToEdit);
        setQuestionsToReAnswerList([]);
        setIsEditingComplete(false);
        // Reset blend guard so services don't fire on open — only fire after the user changes something
        // Get user messages and selected product
        const userMessages = item.messages.filter(msg => msg.type === MESSAGE_TYPE_ENUM.user);
        const selectedProductItem = getSelectedProductFromMessages(userMessages);
        setSelectedProduct(selectedProductItem);
        setEditedProduct(selectedProductItem);

        setIsEnableBlend(false);


        // Get all current answers from the configuration
        const currentAnswers = getAnswersFromMessages(userMessages);

        // Get the current answer for the question being edited
        const currentMessage = userMessages.find((msg) => msg.question?.questionId === questionIdToEdit);
        // Find all dependent questions from the original configuration
        // These will be removed from the original and replaced with new answers
        if (currentMessage?.response?.answer && currentMessage.response.answer.length > 0) {
            const dependentQuestions = getNextVisibleDependentQuestion(
                questionIdToEdit,
                questionFlow,
                {
                    [currentMessage.question.category]: currentMessage.response.answer
                },
                false,
                selectedProductItem
            );

            // Store the question IDs that need to be removed from original messages
            const dependentQuestionIds = dependentQuestions.map(q => q.questionId);
            setQuestionsToRemoveFromOriginal(dependentQuestionIds);
        } else {
            setQuestionsToRemoveFromOriginal([]);
        }

        // Re-hydrate options from questionFlow — stored messages have options stripped to save
        // localStorage quota (see slimMessages). questionFlow always has the full options list.
        const fullQuestion = questionFlow.find(q => q.questionId === questionIdToEdit) ?? currentMessage?.question;

        // Filter question options based on selected product and current answers
        const filteredQuestion = fullQuestion
            ? filterQuestionOptions(
                fullQuestion,
                currentAnswers,
                selectedProductItem,
                getFormValuesFromMessages(userMessages),
            )
            : {} as IQuestion;

        // Create the initial question message with current selection and filtered options
        const initialMessage: IAssistantMessage = {
            id: `asistant_${Date.now()}_${Math.random().toString(36)}`,
            message: filteredQuestion.question ?? "",
            type: 'assistant',
            question: filteredQuestion,
            response: currentMessage?.response || { answer: [], selectedOptionsIds: [], form: {} }
        };

        // Resolve glazing-conditional prices on the initial bot message so option cards
        // show a single correct price instead of both the base price and priceModifier.
        const resolved = applyGlazingPriceToMessages([...userMessages, initialMessage], questionFlow);
        const messages = resolved[resolved.length - 1] ?? initialMessage;

        setDraftMessages([messages]);
    };
    const closeDrawer = () => {
        // Reset all drawer state
        setIsOpen(false);
        setEditingItem(null);
        setEditingItemId(null);
        setEditingQuestionId(null);
        setDraftMessages([]);
        setQuestionsToReAnswerList([]);
        setQuestionsToRemoveFromOriginal([]);
        setIsEditingComplete(false);

        // Reset product states
        setEditedProduct(undefined);
        setSelectedProduct(undefined);

        // Reset fire egress consent state
        setEgressConsentState(null);
        setEgressConsentAfterQuestionId(null);
        pendingDependentRef.current = null;

        // Reset preview modal state
        setIsPreviewModalOpen(false);

        // Reset blend mode to default
        setCurrentBlendMode('offline');

        // Reset cart state
        setCart(undefined);

        // Reset blend guard and services
        setIsEnableBlend(false);
        grillBlend.reset();
        dbrBlend.reset();

    };

    const saveChanges = async (onSave: (updatedItem: IWindowCartItem) => void | Promise<void>) => {
        if (!editingItem || !editingItemId || !editingQuestionId) return;

        // Internal guard — not just the Update Configuration button's disabled
        // state. dbr/grill/colorize isApplying (see the exposed `state.isBlending`
        // below) only reflects the UI's last render; a click that slips in during
        // the debounce/race window, or any other caller of saveChanges, would
        // otherwise fall through to the blendedImageUrl fallback chain further
        // down and save a stale (previous or pre-edit) image as if it were current.
        if (dbrBlend.isApplying || grillBlend.isApplying || colorizeBlend.isApplying) {
            message.info('The window image is still being generated — please wait a moment before saving.');
            return;
        }

        // Step 2: Build set of question IDs to remove (to avoid duplicates)
        // Includes: editing question + dependent questions found during editing
        const questionIdsToRemove = new Set([editingQuestionId, ...questionsToRemoveFromOriginal]);
        // Step 3: Remove ALL questions that should be removed AND all surge messages
        // Remove if: (1) at or after editing step, OR (2) in the specific removal list
        const filteredOriginalMessages = editingItem.messages.filter(msg => {
            // Remove all surge price messages - we'll recalculate them
            if (msg.type === 'price-surge') {
                return false;
            }

            // Remove if question is in the specific removal list (includes parent questions, etc.)
            if (msg.question?.questionId && questionIdsToRemove.has(msg.question.questionId)) {
                return false;
            }
            return true;
        });


        // Step 3: Merge draft messages (new answers) with filtered original messages.
        // Deduplicate: keep draftMessages version when original and draft share the same
        // questionId (e.g. auto-injected coupling clip Q0157) OR the same stable id
        // (e.g. dimension_config_alert which has no questionId).
        const draftQuestionIds = new Set(
            draftMessages.filter(m => m.question?.questionId).map(m => m.question.questionId)
        );
        const draftStableIds = new Set(
            draftMessages.filter(m => m.id && !m.question?.questionId).map(m => m.id)
        );
        const deduplicatedOriginal = filteredOriginalMessages.filter(m => {
            if (m.question?.questionId && draftQuestionIds.has(m.question.questionId)) return false;
            if (m.id && !m.question?.questionId && draftStableIds.has(m.id)) return false;
            return true;
        });
        const mergedMessages = [...deduplicatedOriginal, ...draftMessages];

        // Step 4: Remove old dependent questions that are no longer valid
        // This handles cases where:
        // - An answer changed (e.g., "Glass Options" → "Unglazed")
        // - Product changed (e.g., different product has different questions for same category)
        // - Old dependent questions need to be removed
        const originalUserMessages = editingItem.messages.filter(msg => msg.type === MESSAGE_TYPE_ENUM.user);
        const cleanedMessagesUnsorted = removeOldDependentQuestions(
            mergedMessages,
            originalUserMessages,
            questionFlow,
            selectedProduct // Pass current product to filter product-specific questions
        );

        // Sort cleaned messages by question step to maintain proper order
        const cleanedMessagesSorted = cleanedMessagesUnsorted.sort((a, b) => {
            const stepA = a.question?.step ?? 0;
            const stepB = b.question?.step ?? 0;
            return stepA - stepB;
        });

        // Angled Cill Horn → bay_poles sync.
        // For angle-based bays: recalculate from angle fields + cill horn bonus (+2 if Yes).
        // For non-angle bays: trust the existing bay_poles value (already adjusted by ±2 in handleMultipleChoice).
        const angledCillAnswer = cleanedMessagesSorted.find(
            m => m.type === 'user' && m.question?.optionType === OPTION_TYPE_ANGLED_CILL_HORN
        );
        const dimIdx = cleanedMessagesSorted.findIndex(
            m => m.type === 'user' && m.question?.optionType === DIMENSION_OPTION
        );
        if (dimIdx !== -1 && angledCillAnswer) {
            const dimMsg = cleanedMessagesSorted[dimIdx];
            const isAngledYes = angledCillAnswer?.response?.answer?.[0]?.responseValue === 'yes';
            const { bayPoles: angleBayPoles, hasAngles } = calculateBayPolesAndCouplingClips(
                (dimMsg.response?.form ?? {}) as Record<string, unknown>,
                false,
            );
            if (hasAngles) {
                const newBayPoles = angleBayPoles + (isAngledYes ? 2 : 0);
                const updatedForm = { ...dimMsg.response?.form, bay_poles: newBayPoles };
                cleanedMessagesSorted[dimIdx] = {
                    ...dimMsg,
                    response: { ...dimMsg.response, form: updatedForm },
                };
            }
            // No-angle fallback: bay_poles was already maintained via handleMultipleChoice delta logic.
        }

        // Coupling clip normalisation: reconstructAssistantMessages embeds the
        // coupling clip option inside the dimension form's answer, but the
        // original cart creation stores it as a separate message via
        // buildCouplingClipMessage. Ensure a separate coupling clip message
        // always exists so transformAToSubQuoteProducts produces stable diff keys
        // (step/optionname/name) that match what was saved during quote creation.
        const normalizedMessages = (() => {
            const dimIdx = cleanedMessagesSorted.findIndex(
                m => m.type === 'user' && m.question?.optionType === DIMENSION_OPTION,
            );
            if (dimIdx === -1) return cleanedMessagesSorted;

            const dimMsg = cleanedMessagesSorted[dimIdx];
            const clipOptionInDim = dimMsg.response?.answer?.find(
                a => a.optionType === OPTION_TYPE_COUPLING_CLIP,
            );
            if (!clipOptionInDim) return cleanedMessagesSorted;

            // Extract quantity from form or default to 1
            const formKey = clipOptionInDim.responseValue || clipOptionInDim.optionId;
            const clipCount = Number(dimMsg.response?.form?.[formKey] ?? 1);

            // Remove coupling clip from the dimension form answer + form
            const strippedDimMsg = {
                ...dimMsg,
                response: {
                    ...dimMsg.response,
                    answer: dimMsg.response.answer.filter(a => a.optionType !== OPTION_TYPE_COUPLING_CLIP),
                    selectedOptionsIds: dimMsg.response.selectedOptionsIds?.filter(
                        id => id !== clipOptionInDim.optionId,
                    ) ?? [],
                    form: Object.fromEntries(
                        Object.entries(dimMsg.response.form ?? {}).filter(([k]) => k !== formKey),
                    ),
                },
            };

            // Build a proper standalone coupling clip message (stable step + category)
            const properClipMsg = buildCouplingClipMessage(clipCount, questionFlow);

            // Replace dimension message and remove any stale coupling clip message,
            // then append the fresh one.
            const withoutClip = cleanedMessagesSorted.map((m, i) =>
                i === dimIdx ? strippedDimMsg : m,
            ).filter(m => m.id !== COUPLING_CLIP_MSG_ID);

            return properClipMsg ? [...withoutClip, properClipMsg] : withoutClip;
        })();

        // Coupling clips are only valid for Bay and Coupled windows.
        // If the saved product is Standard or Arch, remove any leftover clip message.
        const effectiveProduct = selectedProduct ?? editedItemProduct;
        const savedProductSupportsCouplingClip =
            effectiveProduct?.producttypecode === ProductStructureType.Bays ||
            effectiveProduct?.producttypecode === ProductStructureType.Coupled;
        const normalizedMessagesClean = savedProductSupportsCouplingClip
            ? normalizedMessages
            : normalizedMessages.filter(m => m.id !== COUPLING_CLIP_MSG_ID);

        // Re-apply render-time price transformations across every message so
        // that per-sash / per-metre options reflect the latest dimensions and
        // product even if their question wasn't formally re-asked as a
        // dependency (e.g. the user only edited Q0005 dimensions).
        const rebakedMessages = rebakeMessagesWithLatestState(normalizedMessagesClean, questionFlow, selectedProduct as IQuestionOption);
        // Apply glazing-conditional pricing so window type / variation base price
        // reflects the saved glazing selection before the final price is calculated.
        const cleanedMessages = applyGlazingPriceToMessages(rebakedMessages, questionFlow);

        let newSurgeMessages = evaluateAllSurgePricesFromMessages(cleanedMessages, questionFlow, formatPrice, marginPercentage);

        // Specials Foil Surcharge is a one-off flat charge per quote.
        // Only strip it from this item if:
        //  1. This item did NOT originally carry the foil charge, AND
        //  2. Another window already carries it.
        // If this item was the original owner (already had it before editing), keep it.
        const thisItemHadFoilCharge = (editingItem?.messages ?? []).some(m =>
            m.type === MESSAGE_TYPE_ENUM.priceSurge &&
            (m.surgeItems ?? []).some((s: ISurgeItem) => s.resolverValue === FOIL_SURCHARGE_RESOLVER)
        );
        if (!thisItemHadFoilCharge) {
            const otherItems = (cart?.windowConfiguration ?? []).filter(w => w.id !== editingItemId);
            const foilChargeOwnedByOther = otherItems.some(w =>
                w.messages.some(m =>
                    m.type === MESSAGE_TYPE_ENUM.priceSurge &&
                    (m.surgeItems ?? []).some((s: ISurgeItem) => s.resolverValue === FOIL_SURCHARGE_RESOLVER)
                )
            );
            if (foilChargeOwnedByOther) {
                newSurgeMessages = newSurgeMessages
                    .map(m => ({
                        ...m,
                        surgeItems: (m.surgeItems ?? []).filter((s: ISurgeItem) => s.resolverValue !== FOIL_SURCHARGE_RESOLVER),
                    }))
                    .filter(m => (m.surgeItems ?? []).length > 0);
            }
        }

        // Filter out old surge messages from cleanedMessages (added during answer flow)
        // and append freshly evaluated surge messages to prevent duplicates
        const messagesWithoutOldSurges = cleanedMessages.filter(m => m.type !== MESSAGE_TYPE_ENUM.priceSurge);
        const messagesWithUpdatedSurge = [...messagesWithoutOldSurges, ...newSurgeMessages];
        // Step 6: Recalculate total price
        const updatedPrice = calculatePriceFromMessages(messagesWithUpdatedSurge);


        const updatedItem: IWindowCartItem = {
            ...editingItem,
            messages: messagesWithUpdatedSurge,
            price: updatedPrice,
            // Never fall back to grillBlend.lastTaskStatus when grill is None (any
            // level) — it can still hold a stale grilled result_url left over from
            // before the edit, which would re-introduce the grill into the saved
            // image.
            //
            // editingItem.blendedImageUrl must come before the raw template fallback:
            // the whole template pipeline (dbrBlend/colorizeBlend/grillBlend) is gated
            // on isEnableBlend, which stays false unless a blend-affecting answer
            // (grill/color/product/DBR) actually changed. So when the user edits
            // something unrelated, every lastTaskStatus here is null and this chain
            // used to fall straight to the bare product template, discarding the
            // window's already-saved blended image. Falling back to the existing
            // blended image first keeps it intact when nothing blend-related changed;
            // the raw template is only used when there was never a blended image to
            // begin with.
            blendedImageUrl: (grillNeedsToRun ? grillBlend.lastTaskStatus?.result_url : undefined)
                ?? dbrBlend.lastTaskStatus?.result_url ?? colorizeBlend?.lastTaskStatus?.result_url
                ?? editingItem.blendedImageUrl ?? colorizedTemplateUrl ?? resolvedTemplateUrl ?? selectedProduct?.thumbnail ?? "",
            onlineBlendedImageUrl: editingItem.onlineBlendedImageUrl,
            fireEgressConsent: (egressConsentState === 'accepted' || egressConsentState === 'rejected')
                ? egressConsentState
                : editingItem.fireEgressConsent,
        };


        // Step 7: Save and close (await so async saves finish before closing).
        // onSave can throw (network error, a bug in the caller's own save/
        // reconcile logic) — without a try/catch that exception just propagates
        // out uncaught, closeDrawer() never runs, and the drawer is left stuck
        // open with no visible error at all. Surface it instead so a real
        // failure is never silent, and keep the drawer open on error so the
        // user can retry rather than losing the edit.
        try {
            await Promise.resolve(onSave(updatedItem));
            closeDrawer();
        } catch (error) {
            console.error('[ConfigurationDrawerContext] saveChanges failed:', error);
            message.error('Failed to save the configuration. Please try again.');
        }
    };

    const discardChanges = () => {
        closeDrawer(); // closeDrawer will reset all state including blend service
    };

    const openPreviewModal = () => {
        setIsPreviewModalOpen(true);
    };

    const closePreviewModal = () => {
        setIsPreviewModalOpen(false);
    };

    const setBlendMode = (mode: 'offline' | 'online') => {
        setCurrentBlendMode(mode);
    };

    // ========================================================================
    // CONTEXT VALUE
    // ========================================================================

    // Check if product has changed
    const hasProductChanged = Boolean(
        editedItemProduct &&
        selectedProduct &&
        editedItemProduct.optionId !== selectedProduct.optionId
    );

    const state: ConfigurationDrawerState = {
        isOpen,
        editingItem,
        editingItemId,
        editingQuestionId,
        draftMessages,
        questionsToReAnswerList,
        questionsToRemoveFromOriginal,
        isEditingComplete,
        isPreviewModalOpen,
        // Falls back through the grill/colorize/DBR pipeline directly — this
        // drawer's edit flow never has a property/house photo to AI-blend onto,
        // so the live preview is just this template chain.
        blendedImageUrl: grillBlend.grillImageUrl ?? colorizeBlend.colorizedUrl ?? dbrBlend.dbrImageUrl ?? resolvedTemplateUrl ?? null,
        onLineBlendImageUrl: null,
        isBlending: dbrBlend.isApplying || grillBlend.isApplying || colorizeBlend.isApplying,
        hasProductChanged,
        config: config,
        questionFlow,
        marginPercentage: marginPercentage ?? 0,
        egressConsentState,
        selectedProduct: selectedProduct ?? editedItemProduct,
    };

    const method: ConfigurationDrawerMethods = {
        openDrawer,
        closeDrawer,
        handleSingleSelect,
        handleMultipleChoice,
        handleFormSubmit,
        handleTextInput,
        saveChanges,
        discardChanges,
        openPreviewModal,
        closePreviewModal,
        setBlendMode,
        acceptEgressConsent,
        rejectEgressConsent,
    };

    const value: ConfigurationDrawerContextType = { state, method };

    return (
        <ConfigurationDrawerContext.Provider value={value}>
            {children}
        </ConfigurationDrawerContext.Provider>
    );
};

export const useConfigurationDrawer = (): ConfigurationDrawerContextType => {
    const context = useContext(ConfigurationDrawerContext);
    if (!context) {
        throw new Error('useConfigurationDrawer must be used within a ConfigurationDrawerProvider');
    }
    return context;
};
